/**
 * MuellePage — Operación de muelle del día (mobile-first, búsqueda primero).
 *
 * Diseño:
 *   - Search-first: input grande en top, filtros chip horizontales scrolleables.
 *   - Cards verticales optimizadas para smartphone (tap target ≥56px).
 *   - Bottom sheets para detalle / cobro / check-in / walk-in.
 *   - Optimistic UI vía React Query.
 *   - Resiliencia de red: idb-keyval queue + retry transparente.
 *   - Polling de 30s en foreground.
 */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box, Typography, TextField, InputAdornment, IconButton, Chip, Card, CardContent, useMediaQuery, useTheme,
  Avatar, Button, Fab, CircularProgress, FormControl, InputLabel, Select, MenuItem,
  RadioGroup, FormControlLabel, Radio, Alert, Tooltip, Divider, Skeleton, Collapse, Stack, Snackbar,
} from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import PersonIcon from '@mui/icons-material/Person';
import GroupsIcon from '@mui/icons-material/Groups';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import EventBusyIcon from '@mui/icons-material/EventBusy';
import UndoIcon from '@mui/icons-material/Undo';
import EditIcon from '@mui/icons-material/Edit';
import BottomSheet from '../common/BottomSheet';
import EditarReservaSheet from '../booking/EditarReservaSheet';
import ConfirmDialog from '../common/ConfirmDialog';
import useToast from '../common/useToast';
import { queueMutation, isOnline, onQueueChange, queueLength } from '../../utils/offlineQueue';
import { formatNombre } from '../../utils/formatNombre';
import EvidenciaCaptura from './EvidenciaCaptura';
import RecepcionTablaDensa from './RecepcionTablaDensa';

const API_BASE = process.env.REACT_APP_API_URL || 'https://turismo.nara.chat/api';

function getEmpresaId() {
  // El superadmin puede cambiar de empresa desde el sidebar (Layout escribe
  // `empresa_activa` en localStorage). Respetar ese valor por encima de
  // `user.company_id` para no quedar fuera de sync con el selector activo.
  const activa = localStorage.getItem('empresa_activa');
  if (activa) return activa;
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.company_id || u.empresa_id || '1'; }
  catch { return '1'; }
}
function getUserId() {
  try { const u = JSON.parse(localStorage.getItem('user') || '{}'); return u.id || u.user_id || null; }
  catch { return null; }
}
function getUserName() {
  try {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    const partes = [u.name, u.lastname].filter(Boolean);
    return partes.join(' ').trim() || u.email || null;
  } catch { return null; }
}
function getAuthHeaders() {
  const t = localStorage.getItem('token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}
// Normaliza para búsqueda: quita diacríticos (tildes) y baja a lowercase.
// Usa rango Unicode U+0300-U+036F (Combining Diacritical Marks) en vez de
// \p{Diacritic}/u porque ese último crashea en Safari iOS antiguo y algunos
// WebViews — lo que hacía que el filtrado quedara mudo en móvil.
function norm(s) {
  try {
    return (s || '').toString().normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
  } catch (_) {
    return (s || '').toString().toLowerCase();
  }
}

// Fecha de HOY en zona horaria LOCAL del dispositivo (YYYY-MM-DD).
// new Date().toISOString() devuelve UTC, lo que en la noche colombiana
// da "ma\u00f1ana" y el backend no devuelve reservas \u2192 KPIs en cero.
function todayLocal() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function fmtMoney(n) {
  if (n == null) return '—';
  return '$' + new Intl.NumberFormat('es-CO', { minimumFractionDigits: 0 }).format(Number(n));
}
function fmtPax(r) {
  const parts = [];
  if (r.adultos) parts.push(`${r.adultos}A`);
  if (r.ninos) parts.push(`${r.ninos}N`);
  if (r.bebes) parts.push(`${r.bebes}B`);
  return parts.join(' · ') || '—';
}

// Estado visual de la reserva (color de borde + chip)
// Regla clave: si saldo_pendiente>0 pero saldo_a_cargo_de='agencia' → CxC.
// La agencia lo asume contra su cupo; NO se cobra al turista en muelle y NO
// se bloquea el embarque. La liquidación ocurre después desde el dashboard CxC.
function esCxCAgencia(r) {
  return (r.saldo_pendiente || 0) > 0 && (r.saldo_a_cargo_de === 'agencia');
}

// Monto que realmente se cobra al turista en muelle. Cuando la reserva es
// mixta (parte CxC agencia + parte muelle), el backend expone
// `saldo_a_cobrar_muelle` = saldo_muelle_monto. Cuando NO hay split cae a
// `saldo_pendiente`. Usar este helper en TODOS los lugares del checkin —
// nunca `saldo_pendiente` directo, que incluye la parte de CxC agencia.
function montoAcobrarMuelle(r) {
  const v = r?.saldo_a_cobrar_muelle;
  return (v !== undefined && v !== null) ? Number(v) : Number(r?.saldo_pendiente || 0);
}
function tieneSplitAgencia(r) {
  return r?.saldo_muelle_monto !== undefined && r?.saldo_muelle_monto !== null;
}

// Devuelve el desglose exacto de lo que queda pendiente en la reserva:
//   { muelle: N, cxc: N }
// Regla:
//   - Si hay split (saldo_muelle_monto NO null): muelle = saldo_muelle_monto,
//     cxc = cxc_agencia_monto. Ambos pueden ser 0 o >0 independientemente.
//   - Si NO hay split y saldo_a_cargo_de='agencia' → todo el saldo es CxC.
//   - Si NO hay split y saldo_a_cargo_de='muelle' (o null) → todo muelle.
// Ninguna reserva puede tener plata pendiente que NO caiga en uno de los dos.
function saldosDeReserva(r) {
  if (!r) return { muelle: 0, cxc: 0 };
  if (r.saldo_muelle_monto !== undefined && r.saldo_muelle_monto !== null) {
    return {
      muelle: Number(r.saldo_muelle_monto || 0),
      cxc: Number(r.cxc_agencia_monto || 0),
    };
  }
  const pendiente = Number(r.saldo_pendiente || 0);
  if ((r.saldo_a_cargo_de || '') === 'agencia') {
    return { muelle: 0, cxc: pendiente };
  }
  return { muelle: pendiente, cxc: 0 };
}

// Chips apilados para mostrar en la lista del muelle:
//   - Muelle > 0 → chip amarillo "Debe $X" (turista tiene que pagar)
//   - CxC > 0   → chip azul informativo "CxC $X" (agencia asume, no cobra muelle)
//   - Ambos     → apilados verticalmente
//   - Ninguno   → nada (todo saldado)
function SaldosChips({ r, dense = false, align = 'flex-end' }) {
  const { muelle, cxc } = saldosDeReserva(r);
  if (muelle <= 0 && cxc <= 0) return null;
  const sz = dense ? { fs: 10, py: 0.15, px: 0.75 } : { fs: 11, py: 0.35, px: 1 };
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column',
               alignItems: align, gap: 0.35 }}>
      {muelle > 0 && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center',
                   gap: 0.4, px: sz.px, py: sz.py, borderRadius: 1,
                   bgcolor: '#fef3c7', color: '#a16207',
                   fontSize: sz.fs, fontWeight: 800,
                   letterSpacing: 0.2 }}>
          Muelle {fmtMoney(muelle)}
        </Box>
      )}
      {cxc > 0 && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center',
                   gap: 0.4, px: sz.px, py: sz.py, borderRadius: 1,
                   bgcolor: '#e0f2fe', color: '#0369a1',
                   fontSize: sz.fs, fontWeight: 800,
                   letterSpacing: 0.2 }}>
          CxC agencia {fmtMoney(cxc)}
        </Box>
      )}
    </Box>
  );
}

function reservaEstadoUI(r) {
  if (r.estado_embarque === 'embarcado') {
    return { color: '#10b981', label: 'Embarcado', bg: '#d1fae5' };
  }
  if (r.estado_embarque === 'no_show') {
    return { color: '#64748b', label: 'No-show', bg: '#e2e8f0' };
  }
  const { muelle, cxc } = saldosDeReserva(r);
  // Prioridad: si hay parte muelle sin cobrar, ese es el estado urgente.
  // Si solo queda CxC agencia (parte muelle ya cobrada o nunca hubo),
  // el chip principal dice "CxC agencia — listo para embarcar" en azul info.
  if (muelle > 0) {
    return { color: '#f59e0b', label: `Debe ${fmtMoney(muelle)}`, bg: '#fef3c7' };
  }
  if (cxc > 0) {
    return { color: '#0891b2', label: `CxC agencia ${fmtMoney(cxc)}`, bg: '#e0f2fe' };
  }
  if (r.estado_embarque === 'cobrado') {
    return { color: '#0ea5e9', label: 'Listo para embarcar', bg: '#dbeafe' };
  }
  return { color: '#94a3b8', label: 'Sin llegar', bg: '#f1f5f9' };
}

// ============================================================================
// MAIN
// ============================================================================
export default function MuellePage() {
  const empresaId = getEmpresaId();
  const queryClient = useQueryClient();
  const { toast, ToastContainer } = useToast();
  // Detección de red para ajustar el polling y reducir tráfico en celular.
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [fecha, setFecha] = useState(() => todayLocal());
  const [search, setSearch] = useState('');
  const [searchDebounced, setSearchDebounced] = useState('');
  const [empresaNombre, setEmpresaNombre] = useState('');
  const [salidaFiltro, setSalidaFiltro] = useState('todas');
  const [estadoFiltro, setEstadoFiltro] = useState('todos');

  const [detalleReserva, setDetalleReserva] = useState(null);   // bottom sheet detalle
  const [cobroReserva, setCobroReserva] = useState(null);       // bottom sheet cobro
  const [walkinOpen, setWalkinOpen] = useState(false);
  const [confirmEmbarcar, setConfirmEmbarcar] = useState(null);
  const [confirmNoShow, setConfirmNoShow] = useState(null);
  const [confirmReset, setConfirmReset] = useState(null);
  const [editarReserva, setEditarReserva] = useState(null);
  const [online, setOnline] = useState(isOnline());
  const [queueCount, setQueueCount] = useState(0);

  // Cargar nombre de la empresa activa para mostrarlo en el header.
  // Evita el "no me funciona" cuando el selector quedó en otra empresa.
  useEffect(() => {
    let cancel = false;
    fetch(`${API_BASE}/admin/empresas`, { headers: getAuthHeaders() })
      .then(r => r.json())
      .then(d => {
        if (cancel) return;
        const arr = Array.isArray(d) ? d : (d.empresas || []);
        const e = arr.find(x => String(x.id) === String(empresaId));
        setEmpresaNombre(e?.company || e?.nombre || `Empresa ${empresaId}`);
      }).catch(() => setEmpresaNombre(`Empresa ${empresaId}`));
    return () => { cancel = true; };
  }, [empresaId]);

  // Debounce de la búsqueda para no spamear el endpoint global
  useEffect(() => {
    const t = setTimeout(() => setSearchDebounced(search.trim()), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Listener de conectividad + cola offline
  useEffect(() => {
    const onUp = () => setOnline(true);
    const onDown = () => setOnline(false);
    window.addEventListener('online', onUp);
    window.addEventListener('offline', onDown);
    queueLength().then(setQueueCount);
    const unsub = onQueueChange(() => queueLength().then(setQueueCount));
    return () => { window.removeEventListener('online', onUp); window.removeEventListener('offline', onDown); unsub(); };
  }, []);

  // ── Query: snapshot del día
  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['muelle', empresaId, fecha],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/turismo/muelle/dia?empresa_id=${empresaId}&fecha=${fecha}`, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('Error cargando muelle');
      return r.json();
    },
    // Latencia móvil: polling más espaciado (60s vs 30s desktop) para
    // ahorrar datos celulares + stale-while-revalidate corto para que la
    // UI siga reactiva sin pedir datos cada cambio de filtro.
    refetchInterval: online ? (isMobile ? 60000 : 30000) : false,
    staleTime: isMobile ? 30000 : 10000,
    refetchOnWindowFocus: !isMobile,  // En móvil, evita refetch al volver al tab
    keepPreviousData: true,            // Muestra datos viejos mientras llega el refresh
  });

  // Lista plana de reservas para filtrar/buscar
  const todasReservas = useMemo(() => {
    if (!data) return [];
    return data.pasadias.flatMap(s =>
      s.reservas.map(r => ({ ...r, _salida: s }))
    );
  }, [data]);

  const reservasFiltradas = useMemo(() => {
    const q = norm(search.trim());
    return todasReservas.filter(r => {
      if (salidaFiltro !== 'todas' && r._salida.salida_id !== parseInt(salidaFiltro)) return false;
      if (estadoFiltro === 'pendiente_pago' && !(montoAcobrarMuelle(r) > 0 && !esCxCAgencia(r))) return false;
      if (estadoFiltro === 'sin_checkin' && r.estado_embarque === 'embarcado') return false;
      if (estadoFiltro === 'embarcados' && r.estado_embarque !== 'embarcado') return false;
      if (estadoFiltro === 'no_show' && r.estado_embarque !== 'no_show') return false;
      if (q) {
        const hay = norm(`${r.codigo_reserva} ${r.nombre_cliente} ${r.telefono || ''} ${r.agencia_nombre || ''} ${r._salida.paquete_nombre}`);
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [todasReservas, search, salidaFiltro, estadoFiltro]);

  // ── Búsqueda global: se activa cuando el usuario escribe ≥2 caracteres.
  //    Devuelve coincidencias en cualquier fecha, ordenadas por proximidad a hoy.
  const { data: searchData, isFetching: searchLoading } = useQuery({
    queryKey: ['muelle-search', empresaId, searchDebounced],
    queryFn: async () => {
      const u = `${API_BASE}/turismo/muelle/buscar?empresa_id=${empresaId}&q=${encodeURIComponent(searchDebounced)}`;
      const r = await fetch(u, { headers: getAuthHeaders() });
      if (!r.ok) throw new Error('Error en búsqueda');
      return r.json();
    },
    enabled: searchDebounced.length >= 2,
    staleTime: 15000,
  });

  // Resultados globales que NO están en el día actual → sección "Otras fechas"
  const resultadosOtrasFechas = useMemo(() => {
    if (!searchDebounced || !searchData?.resultados) return [];
    return searchData.resultados.filter(r => r.fecha_salida !== fecha);
  }, [searchData, searchDebounced, fecha]);

  // ── Mutaciones con optimistic UI
  const invalidateMuelle = () => {
    queryClient.invalidateQueries({ queryKey: ['muelle', empresaId, fecha] });
    // Un cambio en muelle (check-in, cobro, no-show, walk-in) también afecta
    // los KPIs del día y la lista global de reservaciones que consume
    // Booking. Sin este invalidate, saltar a /booking desde /muelle mostraba
    // datos viejos hasta un reload duro del navegador.
    queryClient.invalidateQueries({ queryKey: ['booking'] });
  };

  // Handler común para 409 (conflicto de versión): otro coordinador editó la
  // reserva entre que el operador la vio y apretó el botón. Forzar refetch
  // del día completo + toast claro con el detail del backend.
  const handleConflict409 = (err) => {
    invalidateMuelle();
    toast.error(err?.message || 'Otra persona acaba de modificar esta reserva. Datos actualizados.', { duration: 6000 });
  };

  const mEmbarcar = useMutation({
    mutationFn: async ({ reservacionId, embarcacionId, version }) => {
      // Propagar usuario que hace el check-in para que quede en auditoría.
      // Sin esto el timeline mostraría "Embarcado…" sin operador.
      // expected_version: si otro coordinador ya editó esta reserva entre que
      // el operador la vio en pantalla y apretó el botón, el backend devuelve
      // 409 y caemos al handler que refresca + avisa.
      const body = {
        ...(embarcacionId ? { embarcacion_id: embarcacionId } : {}),
        usuario_id: getUserId(),
        usuario_nombre: getUserName(),
        expected_version: version,
      };
      const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${reservacionId}/embarcar`,
        { method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body) });
      if (!r.ok) {
        if (!isOnline()) {
          await queueMutation({
            url: `${API_BASE}/turismo/muelle/reservas/${reservacionId}/embarcar`,
            method: 'PATCH',
            body: JSON.stringify(body),
            label: `Check-in ${reservacionId}`,
          });
          return { queued: true };
        }
        const errJson = await r.json().catch(()=>({}));
        const e = new Error(errJson.detail || 'Error');
        e.status = r.status;
        throw e;
      }
      return r.json();
    },
    onMutate: async ({ reservacionId: id, embarcacionId }) => {
      // Optimistic UI: marca como embarcado al instante (con bote)
      await queryClient.cancelQueries({ queryKey: ['muelle', empresaId, fecha] });
      const prev = queryClient.getQueryData(['muelle', empresaId, fecha]);
      queryClient.setQueryData(['muelle', empresaId, fecha], (old) => {
        if (!old) return old;
        return {
          ...old,
          pasadias: old.pasadias.map(s => ({
            ...s,
            reservas: s.reservas.map(r => r.id === id ? {
              ...r,
              estado_embarque: 'embarcado',
              embarcacion_asignada_id: embarcacionId || r.embarcacion_asignada_id,
            } : r),
          })),
        };
      });
      return { prev };
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['muelle', empresaId, fecha], ctx.prev);
      if (err?.status === 409) { handleConflict409(err); return; }
      toast.error(err.message || 'Error al hacer check-in');
    },
    onSuccess: (res) => {
      if (res?.queued) toast.info('Sin conexión — se sincronizará cuando vuelva');
      else toast.success('Check-in registrado');
      // No invalidamos — la optimistic update ya refleja el cambio. El refetch
      // pesado solo sucede cuando react-query lo decida por staleTime, evitando
      // re-bajar todo el día cada check-in (importante en red móvil).
    },
  });

  const mNoShow = useMutation({
    mutationFn: async ({ id, version }) => {
      const body = JSON.stringify({ expected_version: version });
      const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${id}/no-show`,
        { method: 'PATCH', headers: getAuthHeaders(), body });
      if (!r.ok) {
        if (!isOnline()) { await queueMutation({ url: `${API_BASE}/turismo/muelle/reservas/${id}/no-show`, method: 'PATCH', body, label: `No-show ${id}` }); return { queued: true }; }
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.detail || 'Error');
        e.status = r.status;
        throw e;
      }
      return r.json();
    },
    onMutate: async ({ id }) => {
      const prev = queryClient.getQueryData(['muelle', empresaId, fecha]);
      queryClient.setQueryData(['muelle', empresaId, fecha], (old) => {
        if (!old) return old;
        return {
          ...old,
          pasadias: old.pasadias.map(s => ({
            ...s,
            reservas: s.reservas.map(r => r.id === id ? { ...r, estado_embarque: 'no_show' } : r),
          })),
        };
      });
      return { prev };
    },
    onError: (e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['muelle', empresaId, fecha], ctx.prev);
      if (e?.status === 409) { handleConflict409(e); return; }
      toast.error(e?.message || 'Error al marcar no-show');
    },
    onSuccess: (res) => {
      if (res?.queued) toast.info('Sin conexión — encolado');
      else toast.success('Marcado no-show');
      // No invalidamos — optimistic update ya basta.
    },
  });

  // Deshacer check-in o no-show: vuelve la reserva a estado pendiente. Útil
  // cuando el coordinador embarcó al cliente equivocado o marcó no-show por
  // error. El endpoint /reset solo cambia estado_embarque y limpia fecha.
  const mReset = useMutation({
    mutationFn: async ({ id, version }) => {
      const body = JSON.stringify({ expected_version: version });
      const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${id}/reset`,
        { method: 'PATCH', headers: getAuthHeaders(), body });
      if (!r.ok) {
        if (!isOnline()) {
          await queueMutation({ url: `${API_BASE}/turismo/muelle/reservas/${id}/reset`, method: 'PATCH', body, label: `Deshacer ${id}` });
          return { queued: true };
        }
        // Levantar el detail del backend (ej. "la salida fue el ... y ya pasó")
        // para que el toast en onError lo muestre legible.
        const err = await r.json().catch(() => ({}));
        const e = new Error(err.detail || 'Error');
        e.status = r.status;
        throw e;
      }
      return r.json();
    },
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: ['muelle', empresaId, fecha] });
      const prev = queryClient.getQueryData(['muelle', empresaId, fecha]);
      queryClient.setQueryData(['muelle', empresaId, fecha], (old) => {
        if (!old) return old;
        return {
          ...old,
          pasadias: old.pasadias.map(s => ({
            ...s,
            reservas: s.reservas.map(r => r.id === id ? { ...r, estado_embarque: 'pendiente' } : r),
          })),
        };
      });
      return { prev };
    },
    onError: (e, _id, ctx) => {
      if (ctx?.prev) queryClient.setQueryData(['muelle', empresaId, fecha], ctx.prev);
      if (e?.status === 409) { handleConflict409(e); return; }
      // Mostrar el detalle del backend si vino — incluye el motivo del 400
      // (ej. "la salida fue el 2026-06-18 y ya pasó"). Antes era genérico.
      toast.error(e?.message || 'Error al deshacer');
    },
    onSuccess: (res) => {
      if (res?.queued) toast.info('Sin conexión — encolado');
      else toast.success('Estado restablecido');
    },
  });

  if (isLoading) return <SkeletonScreen />;

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8fafc', pb: 12 }}>
      {/* Header sticky.
          En teléfono va apilado —título arriba, buscador debajo— porque no hay
          ancho para otra cosa. Desde sm todo cabe en una fila: el buscador se
          estira y los KPIs se van a la derecha, que es donde queda el espacio
          muerto de una pantalla ancha. Cada fila que se ahorra acá es una
          reserva más visible sin desplazar. */}
      <Box sx={{
        position: 'sticky', top: 0, zIndex: 10, bgcolor: '#fff',
        borderBottom: '1px solid #e2e8f0',
        px: { xs: 1.5, sm: 2, lg: 3 }, py: { xs: 1, sm: 1.25 },
      }}>
       <Box sx={{ maxWidth: 1600, mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: { xs: 'flex-start', sm: 'center' },
                   gap: { sm: 2 }, justifyContent: 'space-between', mb: { xs: 0.75, sm: 0 } }}>
          <Box sx={{ minWidth: 0, flex: { xs: 1, sm: '0 0 auto' } }}>
            <Typography sx={{ fontSize: { xs: 16, sm: 17 }, fontWeight: 800, color: '#0f172a', lineHeight: 1.1 }} noWrap>
              Muelle · Hoy
            </Typography>
            {/* Fecha y empresa comparten renglón: dos líneas de 10px que antes
                eran tres. */}
            <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
              {new Date(fecha + 'T00:00:00').toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })}
              {empresaNombre && (
                <Box component="span" sx={{ color: '#0d9488', fontWeight: 700 }}> · {empresaNombre}</Box>
              )}
            </Typography>
          </Box>

          {/* Búsqueda — desde sm vive en la misma fila del título y se estira
              hasta donde empiezan los botones */}
          <Box sx={{ flex: 1, minWidth: 0, display: { xs: 'none', sm: 'block' }, maxWidth: 520 }}>
            <BuscadorMuelle search={search} setSearch={setSearch} />
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
            {!online && (
              <Chip icon={<WifiOffIcon sx={{ fontSize: 14, color: '#f59e0b !important' }} />}
                label={queueCount > 0 ? `${queueCount} pendientes` : 'Sin conexión'}
                size="small" sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, height: 24 }} />
            )}
            <TextField type="date" size="small" value={fecha}
              onChange={e => setFecha(e.target.value)}
              sx={{ width: { xs: 132, sm: 145 }, '& .MuiOutlinedInput-root': { height: 34 },
                    '& input': { fontSize: 12, py: 0.5 } }}
              InputProps={{ startAdornment: <CalendarTodayIcon sx={{ fontSize: 14, color: '#94a3b8', mr: 0.5 }} /> }} />
            <IconButton size="small" onClick={refetch} disabled={isFetching}
              sx={{ color: isFetching ? '#cbd5e1' : '#475569' }}>
              <RefreshIcon sx={{ fontSize: 18, ...(isFetching && { animation: 'spin 1s linear infinite' }) }} />
            </IconButton>
            {/* Atajo "Auditoría del día activo". Abre /trazabilidad con filtros
                precargados (fecha de la pestaña + origen=muelle_app) — el operador
                ve todo lo que pasó en muelle ese día y por quién. */}
            <Tooltip title="Auditoría del día (todo lo que pasó en muelle)">
              <IconButton size="small" component="a"
                href={`/trazabilidad?fecha_desde=${fecha}&fecha_hasta=${fecha}`}
                target="_blank" rel="noopener noreferrer"
                sx={{ color: '#475569' }}>
                <HistoryIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {/* En teléfono el buscador va debajo, a lo ancho */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <BuscadorMuelle search={search} setSearch={setSearch} />
        </Box>

        {/* Filter chips */}
        <Box sx={{ display: 'flex', gap: 0.75, mt: { xs: 0.75, sm: 1 }, overflowX: 'auto', pb: 0.25,
                   '&::-webkit-scrollbar': { display: 'none' }, msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          {[
            { v: 'todas', label: 'Todas', count: todasReservas.length },
            { v: 'pendiente_pago', label: 'Pago pend.', count: todasReservas.filter(r => montoAcobrarMuelle(r) > 0 && !esCxCAgencia(r)).length },
            { v: 'sin_checkin', label: 'Por embarcar', count: todasReservas.filter(r => r.estado_embarque !== 'embarcado' && r.estado_embarque !== 'no_show').length },
            { v: 'embarcados', label: 'Embarcados', count: todasReservas.filter(r => r.estado_embarque === 'embarcado').length },
            { v: 'no_show', label: 'No-show', count: todasReservas.filter(r => r.estado_embarque === 'no_show').length },
          ].map(f => (
            <Chip key={f.v} size="small"
              label={`${f.label}${f.count ? ` · ${f.count}` : ''}`}
              clickable
              onClick={() => setEstadoFiltro(f.v === estadoFiltro ? 'todos' : f.v)}
              sx={{
                bgcolor: estadoFiltro === f.v ? '#0d9488' : '#fff',
                color: estadoFiltro === f.v ? '#fff' : '#475569',
                border: '1px solid', borderColor: estadoFiltro === f.v ? '#0d9488' : '#e2e8f0',
                fontWeight: 600, fontSize: 11, height: 28, flexShrink: 0,
              }}
            />
          ))}
          {/* Filtros por salida */}
          {data?.pasadias?.map(s => (
            <Chip key={s.salida_id} size="small"
              label={`${s.hora_salida ? s.hora_salida.slice(0, 5) : ''} ${s.paquete_nombre.slice(0, 14)}`}
              clickable
              onClick={() => setSalidaFiltro(salidaFiltro === String(s.salida_id) ? 'todas' : String(s.salida_id))}
              sx={{
                bgcolor: salidaFiltro === String(s.salida_id) ? '#0ea5e9' : '#fff',
                color: salidaFiltro === String(s.salida_id) ? '#fff' : '#475569',
                border: '1px solid', borderColor: salidaFiltro === String(s.salida_id) ? '#0ea5e9' : '#e2e8f0',
                fontWeight: 600, fontSize: 11, height: 28, flexShrink: 0,
              }}
            />
          ))}
        </Box>
       </Box>
      </Box>

      {/* KPIs totales. Van fuera del header pegados a él, en una tira que se
          desplaza si no caben; desde md hay ancho de sobra y se ven los cuatro. */}
      {data?.totales && (
        <Box sx={{ display: 'flex', gap: 0.75, px: { xs: 1.5, sm: 2, lg: 3 },
                   py: { xs: 0.75, sm: 1 }, overflowX: 'auto', maxWidth: 1600, mx: 'auto',
                   '&::-webkit-scrollbar': { display: 'none' },
                   msOverflowStyle: 'none', scrollbarWidth: 'none' }}>
          <KpiPill label="Pax esperados" value={data.totales.esperados} color="#475569" />
          <KpiPill label="Pax embarcados" value={data.totales.embarcados} color="#10b981" highlight={data.totales.embarcados > 0} />
          <KpiPill label="Por cobrar" value={fmtMoney(data.totales.saldo_pendiente)} color="#f59e0b" highlight={data.totales.saldo_pendiente > 0} />
          <KpiPill label="Pax no-show" value={data.totales.no_show} color="#dc2626" />
        </Box>
      )}

      {/* Charters del día (alquiler completo de embarcación).
          Se muestran arriba con chip morado para diferenciarlos de pasadías. */}
      {(data?.charters?.length || 0) > 0 && (
        <Box sx={{ px: { xs: 1, sm: 2, lg: 3 }, maxWidth: 1600, mx: 'auto', mb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, mt: 0.5 }}>
            <Chip label="CHARTER" size="small"
              sx={{ bgcolor: '#7c3aed', color: '#fff', fontWeight: 800, fontSize: 10, height: 20 }}
            />
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Alquileres del día · {data.charters.length}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {data.charters.map(c => (
              <CharterDelDiaCard key={c.id} charter={c} />
            ))}
          </Box>
        </Box>
      )}

      {/* Lista de cards */}
      <Box sx={{ px: { xs: 1, sm: 2, lg: 3 }, maxWidth: 1600, mx: 'auto',
                 display: 'flex', flexDirection: 'column', gap: 1 }}>
        {reservasFiltradas.length === 0 ? (
          <Box sx={{ py: 6, textAlign: 'center', color: '#94a3b8' }}>
            <DirectionsBoatIcon sx={{ fontSize: 48, mb: 1 }} />
            <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
              {todasReservas.length === 0 ? 'No hay reservas para esta fecha' : 'Sin resultados con los filtros actuales'}
            </Typography>
            {todasReservas.length === 0 && (
              <Typography sx={{ fontSize: 12, mt: 0.5 }}>
                Toca el botón <strong>+</strong> para registrar un walk-in
              </Typography>
            )}
          </Box>
        ) : (
          /* Mismo componente para desktop (tabla densa) y mobile (cards agrupadas).
             La detección responsive es interna del componente. */
          <RecepcionTablaDensa
            pasadias={data?.pasadias || []}
            search={search}
            estadoFiltro={estadoFiltro}
            pasadiaFiltro={salidaFiltro}
            onSelectReserva={(r) => setDetalleReserva(r)}
          />
        )}

        {/* Resultados de otras fechas — solo aparece cuando hay búsqueda activa */}
        {searchDebounced.length >= 2 && resultadosOtrasFechas.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1, mb: 1 }}>
              <CalendarTodayIcon sx={{ fontSize: 14, color: '#64748b' }} />
              <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Encontrado en otras fechas · {resultadosOtrasFechas.length}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {resultadosOtrasFechas.map(r => (
                <OtraFechaCard key={r.id} reserva={r} onTap={() => setFecha(r.fecha_salida)} />
              ))}
            </Box>
          </Box>
        )}

        {searchDebounced.length >= 2 && searchLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress size={20} sx={{ color: '#94a3b8' }} />
          </Box>
        )}
      </Box>

      {/* FAB Walk-in — desplazado arriba para no tapar el asistente IA (NaraChat
          ocupa bottom:20 right:20 con zIndex 1300). */}
      <Fab onClick={() => setWalkinOpen(true)}
        variant="extended"
        sx={{
          position: 'fixed', bottom: 88, right: 20, zIndex: 1200,
          background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)',
          color: '#fff', boxShadow: '0 8px 24px rgba(13, 148, 136, 0.5)',
          textTransform: 'none', fontWeight: 700, px: 2,
          '&:hover': { background: 'linear-gradient(135deg, #0f766e 0%, #0d9488 100%)' },
        }}>
        <AddIcon sx={{ mr: 0.75 }} />
        Walk-in
      </Fab>

      {/* Bottom Sheets */}
      <DetalleSheet
        reserva={detalleReserva}
        onClose={() => setDetalleReserva(null)}
        onCobrar={() => { setCobroReserva(detalleReserva); setDetalleReserva(null); }}
        onEmbarcar={() => { setConfirmEmbarcar(detalleReserva); setDetalleReserva(null); }}
        onNoShow={() => { setConfirmNoShow(detalleReserva); setDetalleReserva(null); }}
        onReset={() => { setConfirmReset(detalleReserva); setDetalleReserva(null); }}
        onEditar={() => { setEditarReserva(detalleReserva); setDetalleReserva(null); }}
        fecha={fecha}
      />

      <EditarReservaSheet
        reserva={editarReserva}
        onClose={() => setEditarReserva(null)}
        onSuccess={(res) => {
          setEditarReserva(null);
          invalidateMuelle();
          toast.success('Reserva editada' + (res?.cambios?.length ? ` (${res.cambios.length} campo${res.cambios.length > 1 ? 's' : ''})` : ''));
        }}
        onError={(e) => {
          if (e?.status === 409) { setEditarReserva(null); handleConflict409(e); return; }
          toast.error(e?.message || 'Error editando');
        }}
      />

      <CobroSheet
        reserva={cobroReserva}
        onClose={() => setCobroReserva(null)}
        onSuccess={() => { setCobroReserva(null); invalidateMuelle(); toast.success('Cobro registrado'); }}
        onError={(e) => {
          // CobroSheet ahora pasa el Error completo. Si es 409 → conflicto de
          // concurrencia: refrescamos data + toast específico y cerramos el
          // sheet (la versión local ya no sirve para reintentar inmediato).
          const msg = typeof e === 'string' ? e : e?.message;
          if (e?.status === 409) {
            setCobroReserva(null);
            handleConflict409(e);
            return;
          }
          toast.error(msg || 'Error');
        }}
        onQueued={() => { setCobroReserva(null); invalidateMuelle(); toast.info('Sin conexión — encolado'); }}
      />

      <WalkInSheet
        open={walkinOpen}
        salidas={data?.pasadias || []}
        onClose={() => setWalkinOpen(false)}
        onSuccess={() => { setWalkinOpen(false); invalidateMuelle(); toast.success('Walk-in creado'); }}
        onError={msg => toast.error(msg)}
      />

      {/* Captura de evidencia antes del check-in (foto del titular + OCR documento).
          Si la reserva ya tiene evidencia, salta directo a confirmar. */}
      <BottomSheet
        open={!!confirmEmbarcar}
        onClose={() => setConfirmEmbarcar(null)}
        title={confirmEmbarcar?.tiene_evidencia_checkin ? '¿Confirmar check-in?' : 'Check-in con evidencia'}
      >
        {confirmEmbarcar && (
          <EmbarcarBottomSheetContenido
            reserva={confirmEmbarcar}
            onCancel={() => setConfirmEmbarcar(null)}
            onConfirmar={(embarcacionId) => {
              mEmbarcar.mutate({ reservacionId: confirmEmbarcar.id, embarcacionId, version: confirmEmbarcar.version });
              setConfirmEmbarcar(null);
            }}
            onError={(msg) => toast.error(msg)}
          />
        )}
      </BottomSheet>

      <ConfirmDialog
        open={!!confirmNoShow}
        onClose={() => setConfirmNoShow(null)}
        onConfirm={() => { mNoShow.mutate({ id: confirmNoShow.id, version: confirmNoShow.version }); setConfirmNoShow(null); }}
        title="¿Marcar como no-show?"
        message={confirmNoShow ? `${formatNombre(confirmNoShow.nombre_cliente)} no se presentó al embarque. El cupo queda liberado.` : ''}
        confirmText="Sí, no-show"
        severity="warning"
      />

      <ConfirmDialog
        open={!!confirmReset}
        onClose={() => setConfirmReset(null)}
        onConfirm={() => { mReset.mutate({ id: confirmReset.id, version: confirmReset.version }); setConfirmReset(null); }}
        title={confirmReset?.estado_embarque === 'no_show' ? '¿Deshacer no-show?' : '¿Deshacer check-in?'}
        message={confirmReset ? (
          confirmReset.estado_embarque === 'no_show'
            ? `${formatNombre(confirmReset.nombre_cliente)} volverá a estado pendiente y podrá embarcar normalmente.`
            : `${formatNombre(confirmReset.nombre_cliente)} volverá a estado pendiente. Confirma solo si embarcaste por error — el cupo del bote queda asignado hasta que vuelvas a hacer check-in.`
        ) : ''}
        confirmText="Sí, deshacer"
        severity="warning"
      />

      <ToastContainer />

      <style>{`@keyframes spin { 100% { transform: rotate(360deg); } }`}</style>
    </Box>
  );
}

// ============================================================================
// SUB-COMPONENTES
// ============================================================================

function BuscadorMuelle({ search, setSearch }) {
  return (
    <TextField
      size="small" fullWidth value={search}
      onChange={e => setSearch(e.target.value)}
      onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.querySelector('input')?.blur(); } }}
      placeholder="Buscar por código, nombre, teléfono…"
      inputProps={{
        autoCapitalize: 'none',
        autoCorrect: 'off',
        autoComplete: 'off',
        spellCheck: false,
        enterKeyHint: 'search',
        inputMode: 'search',
      }}
      InputProps={{
        startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 20, color: '#94a3b8' }} /></InputAdornment>,
        endAdornment: search ? <IconButton size="small" onClick={() => setSearch('')}><CloseIcon sx={{ fontSize: 16 }} /></IconButton> : null,
      }}
      sx={{ '& .MuiOutlinedInput-root': { bgcolor: '#f1f5f9', borderRadius: 2.5, height: 36,
                                          '& fieldset': { border: 'none' } },
            '& input': { fontSize: 13 } }}
    />
  );
}

function KpiPill({ label, value, color, highlight }) {
  return (
    <Box sx={{
      flexShrink: 0, px: 1.25, py: 0.5, borderRadius: 2,
      display: 'flex', alignItems: 'baseline', gap: 0.75,
      bgcolor: highlight ? `${color}15` : '#fff',
      border: '1px solid', borderColor: highlight ? `${color}40` : '#e2e8f0',
    }}>
      {/* Rótulo y número en la misma línea: la mitad de alto que apilados, y
          en una pantalla de muelle el alto es lo que escasea. */}
      <Typography sx={{ fontSize: 15, fontWeight: 800, color, lineHeight: 1.2 }}>{value}</Typography>
      <Typography sx={{ fontSize: 9.5, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase',
                        letterSpacing: 0.4, whiteSpace: 'nowrap' }}>{label}</Typography>
    </Box>
  );
}

function ReservaCard({ reserva, onTap }) {
  const ui = reservaEstadoUI(reserva);
  return (
    <Card onClick={onTap}
      sx={{
        cursor: 'pointer', borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${ui.color}`,
        '&:active': { transform: 'scale(0.98)' },
        transition: 'transform 0.1s ease',
      }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 0.5 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }} noWrap>
              {formatNombre(reserva.nombre_cliente) || reserva.codigo_reserva}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
              {reserva.codigo_reserva} · {fmtPax(reserva)}
              {reserva.agencia_nombre && ` · ${reserva.agencia_nombre}`}
              {reserva.es_walkin && ' · Walk-in'}
            </Typography>
          </Box>
          <Chip size="small" label={ui.label}
            sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: ui.bg, color: ui.color, flexShrink: 0, ml: 1 }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
            {reserva._salida.hora_salida ? reserva._salida.hora_salida.slice(0, 5) : '—'}
          </Typography>
          <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
            · {reserva._salida.paquete_nombre}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

function OCRPanel({ loading, result, esAplicable }) {
  if (!esAplicable) return null;
  if (loading) {
    return (
      <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0',
                 display: 'flex', alignItems: 'center', gap: 1 }}>
        <CircularProgress size={14} sx={{ color: '#0d9488' }} />
        <Typography sx={{ fontSize: 11, color: '#475569' }}>Validando con IA…</Typography>
      </Box>
    );
  }
  if (!result) return null;

  // No leíble
  if (!result.ok) {
    return (
      <Alert severity="info" icon={false}
        sx={{ mt: 1, fontSize: 11, py: 0.5, '& .MuiAlert-message': { py: 0.5 } }}>
        ⓘ No se pudo leer el comprobante automáticamente. Ingresa la referencia manualmente.
      </Alert>
    );
  }

  const colores = {
    verde:    { bg: '#dcfce7', border: '#86efac', text: '#15803d', icon: '✓' },
    amarillo: { bg: '#fef3c7', border: '#fcd34d', text: '#92400e', icon: '⚠' },
    rojo:     { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b', icon: '⛔' },
    gris:     { bg: '#f1f5f9', border: '#cbd5e1', text: '#475569', icon: 'ⓘ' },
  };
  const c = colores[result.nivel_alerta] || colores.gris;

  return (
    <Box sx={{ mt: 1, p: 1.25, borderRadius: 1.5, bgcolor: c.bg, border: `1px solid ${c.border}` }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
        <Typography sx={{ fontSize: 14 }}>{c.icon}</Typography>
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: c.text }}>
          {result.nivel_alerta === 'verde' && 'Comprobante validado'}
          {result.nivel_alerta === 'amarillo' && 'Diferencia detectada'}
          {result.nivel_alerta === 'rojo' && 'Diferencia importante'}
          {result.nivel_alerta === 'gris' && 'Datos parciales'}
        </Typography>
      </Box>
      {result.monto != null && (
        <Typography sx={{ fontSize: 11, color: c.text }}>
          <strong>Monto:</strong> {fmtMoney(result.monto)}
          {result.coincide_monto === false && (
            <> · cobro {fmtMoney(result.monto_esperado)} (Δ {result.diferencia_pct?.toFixed(1)}%)</>
          )}
        </Typography>
      )}
      {result.referencia && (
        <Typography sx={{ fontSize: 11, color: c.text }}>
          <strong>Ref:</strong> {result.referencia}
          {result.franquicia && ` · ${result.franquicia}`}
          {result.ultimos_4_tarjeta && ` ****${result.ultimos_4_tarjeta}`}
        </Typography>
      )}
      {result.advertencias?.length > 0 && (
        <Typography sx={{ fontSize: 10, color: c.text, mt: 0.5, fontStyle: 'italic' }}>
          {result.advertencias[0]}
        </Typography>
      )}
      {/* Cuenta destino: match único, ambigüedad, o texto sin match */}
      {result.cuenta_destino_texto && (
        <Box sx={{ mt: 0.75, pt: 0.5, borderTop: `1px dashed ${c.border}` }}>
          <Typography sx={{ fontSize: 10.5, color: c.text }}>
            <strong>Cuenta detectada:</strong> "{result.cuenta_destino_texto}"
          </Typography>
          {result.cuenta_destino_match?.cuenta_id && (
            <Typography sx={{ fontSize: 10.5, color: '#7c3aed', fontWeight: 700, mt: 0.25 }}>
              ✨ Match: {result.cuenta_destino_match.cuenta_nombre}
              {' '}(score {(result.cuenta_destino_match.score * 100).toFixed(0)}%) · pre-seleccionada, editable
            </Typography>
          )}
          {!result.cuenta_destino_match?.cuenta_id
            && result.cuenta_destino_match?.candidatos?.length > 0 && (
            <Typography sx={{ fontSize: 10.5, color: '#92400e', mt: 0.25 }}>
              ⚠ Ambiguo. Posibles: {result.cuenta_destino_match.candidatos
                .map(x => `${x.nombre} (${(x.score * 100).toFixed(0)}%)`).join(', ')}
              . Elige manualmente.
            </Typography>
          )}
          {!result.cuenta_destino_match?.cuenta_id
            && (!result.cuenta_destino_match?.candidatos?.length) && (
            <Typography sx={{ fontSize: 10.5, color: '#991b1b', mt: 0.25 }}>
              ⛔ No coincide con ninguna cuenta configurada. Elige manualmente
              o verifica que la cuenta esté registrada.
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
}


function OtraFechaCard({ reserva, onTap }) {
  // Formato de fecha relativa: "mañana", "en 3 días", "hace 2 días"
  const hoy = new Date(todayLocal() + 'T00:00:00');
  const f = new Date(reserva.fecha_salida + 'T00:00:00');
  const diff = Math.round((f - hoy) / 86400000);
  const rel =
    diff === 0 ? 'hoy' :
    diff === 1 ? 'mañana' :
    diff === -1 ? 'ayer' :
    diff > 0 ? `en ${diff} días` : `hace ${Math.abs(diff)} días`;
  const fechaTxt = f.toLocaleDateString('es-CO', { weekday: 'short', day: 'numeric', month: 'short' });
  const esFutura = diff >= 0;

  return (
    <Card onClick={onTap}
      sx={{
        cursor: 'pointer', borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        borderLeft: `4px solid ${esFutura ? '#8b5cf6' : '#94a3b8'}`,
        bgcolor: '#fafafa',
        '&:active': { transform: 'scale(0.98)' }, transition: 'transform 0.1s ease',
      }}>
      <CardContent sx={{ p: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }} noWrap>
              {formatNombre(reserva.nombre_cliente) || reserva.codigo_reserva}
            </Typography>
            <Typography sx={{ fontSize: 11, color: '#64748b' }} noWrap>
              {reserva.codigo_reserva} · {fmtPax(reserva)} · {reserva.paquete_nombre}
            </Typography>
          </Box>
          <Chip size="small" label={rel}
            sx={{ height: 20, fontSize: 10, fontWeight: 700, flexShrink: 0, ml: 1,
                  bgcolor: esFutura ? '#ede9fe' : '#f1f5f9',
                  color: esFutura ? '#7c3aed' : '#64748b' }} />
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
          <CalendarTodayIcon sx={{ fontSize: 12, color: '#94a3b8' }} />
          <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 600 }}>
            {fechaTxt} · {reserva.hora_salida ? reserva.hora_salida.slice(0, 5) : '—'}
          </Typography>
          <Box sx={{ ml: 'auto' }}>
            <SaldosChips r={reserva} dense />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

function DetalleSheet({ reserva, onClose, onCobrar, onEmbarcar, onNoShow, onReset, onEditar, fecha }) {
  if (!reserva) return null;
  const ui = reservaEstadoUI(reserva);
  const yaEmbarcado = reserva.estado_embarque === 'embarcado';
  const yaNoShow = reserva.estado_embarque === 'no_show';
  // "Requiere pago" solo si el saldo va contra el turista. Cuando es CxC
  // (saldo_a_cargo_de='agencia') la agencia lo asume — se puede embarcar sin
  // cobrar en muelle. La liquidación ocurre después via el dashboard CxC.
  const esCxC = esCxCAgencia(reserva);
  // Requiere pago cuando hay parte cobrable en muelle (no toda es CxC agencia).
  const requierePago = montoAcobrarMuelle(reserva) > 0 && !esCxC;
  // Todas las acciones del muelle (cobrar, check-in, no-show, deshacer) se
  // ejecutan SÓLO el mismo día de la salida. Si el operador está consultando
  // un día pasado o futuro, los botones quedan deshabilitados.
  // Futuras: el check-in se hace cuando llega la fecha, no antes.
  // Pasadas: el día ya cerró; modificarlo rompe el cierre de caja / manifiesto.
  const esDiaDeSalida = !!fecha && fecha === todayLocal();
  const tooltipNoEsHoy = fecha && fecha > todayLocal()
    ? `La salida es el ${fecha}. El check-in solo se hace ese día.`
    : `La salida fue el ${fecha} y ya pasó. El día queda inmutable al cierre.`;

  // Editable solo si: creada hoy + no embarcada + no cancelada + sin liquidación.
  // Las mismas reglas que aplica el backend; las duplicamos para deshabilitar
  // el botón con explicación clara sin tener que hacer roundtrip.
  const creadaDia = reserva.created_at ? String(reserva.created_at).slice(0, 10) : null;
  const creadaHoy = !!creadaDia && creadaDia === todayLocal();
  const tieneLiquidacion = !!reserva.liquidacion_id;
  const cancelada = reserva.estado === 'cancelada';
  const puedeEditar = creadaHoy && !yaEmbarcado && !cancelada && !tieneLiquidacion;
  let tooltipNoEditable = '';
  if (!creadaHoy)         tooltipNoEditable = `Solo se puede editar el día de creación (${creadaDia || 's/d'}). Hoy es ${todayLocal()}.`;
  else if (yaEmbarcado)   tooltipNoEditable = 'Esta reserva ya tiene check-in. Deshacelo primero desde el muelle.';
  else if (cancelada)     tooltipNoEditable = 'Una reserva cancelada no se edita. Creá una nueva.';
  else if (tieneLiquidacion) tooltipNoEditable = 'La comisión ya fue liquidada con la agencia. No se puede modificar.';

  return (
    <BottomSheet open={!!reserva} onClose={onClose} title={reserva.codigo_reserva}>
      <Box sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: ui.bg, color: ui.color, width: 48, height: 48 }}>
            <PersonIcon />
          </Avatar>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 17, color: '#0f172a' }} noWrap>{formatNombre(reserva.nombre_cliente)}</Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b' }}>{reserva.telefono || 'Sin teléfono'}</Typography>
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
            <Chip label={ui.label} size="small" sx={{ bgcolor: ui.bg, color: ui.color, fontWeight: 700 }} />
            {/* Editar reserva: solo el día de creación, sin check-in, sin liquidación. */}
            <Tooltip title={puedeEditar ? 'Editar esta reserva' : tooltipNoEditable}>
              <span>
                <Button size="small" startIcon={<EditIcon sx={{ fontSize: 13 }} />}
                  onClick={onEditar} disabled={!puedeEditar}
                  sx={{ textTransform: 'none', fontSize: 11, color: '#0d9488', minWidth: 0, px: 1 }}>
                  Editar
                </Button>
              </span>
            </Tooltip>
          </Box>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
          <InfoTile icon={<GroupsIcon />} label="Pax" value={fmtPax(reserva)} />
          <InfoTile icon={<DirectionsBoatIcon />} label="Tour" value={reserva._salida.paquete_nombre} />
          <InfoTile icon={<AccessTimeIcon />} label="Hora salida" value={reserva._salida.hora_salida ? reserva._salida.hora_salida.slice(0, 5) : '—'} />
          <InfoTile icon={<PaymentIcon />} label="Precio total" value={fmtMoney(reserva.precio_total)} />
        </Box>

        {requierePago && (
          <Alert severity="warning" sx={{ mb: 2, fontSize: 12 }}>
            <strong>Saldo a cobrar en muelle: {fmtMoney(montoAcobrarMuelle(reserva))}</strong><br />
            {tieneSplitAgencia(reserva) && (reserva.cxc_agencia_monto || 0) > 0 && (
              <>
                <span style={{ color: '#0891b2' }}>
                  + CxC agencia: {fmtMoney(reserva.cxc_agencia_monto)} (no se cobra al turista)
                </span><br />
              </>
            )}
            Cobra el saldo antes de hacer check-in.
          </Alert>
        )}

        {reserva.hotel && (
          <Box sx={{ mb: 1.5, fontSize: 12, color: '#475569' }}>
            <strong>Hotel:</strong> {reserva.hotel}
          </Box>
        )}
        {reserva.agencia_nombre && (
          <Box sx={{ mb: 1.5, fontSize: 12, color: '#475569' }}>
            <strong>Agencia:</strong> {reserva.agencia_nombre}
          </Box>
        )}
        {reserva.observaciones && (
          <Box sx={{ mb: 1.5, p: 1.25, bgcolor: '#fef3c7', borderRadius: 1, fontSize: 12, color: '#78350f' }}>
            ⚠ {reserva.observaciones}
          </Box>
        )}

        {/* Historial — ANTES de las acciones. El operador típicamente quiere
            ver qué ha pasado con la reserva antes de decidir si cobrar/embarcar.
            Por eso se sube acá y se expande por defecto si tiene eventos. */}
        <HistorialReserva reservaId={reserva.id} fecha={fecha} />

        {/* Acciones primarias — desactivadas si la salida no es hoy. */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          {!esDiaDeSalida && !yaEmbarcado && !yaNoShow && (
            <Alert severity="info" sx={{ fontSize: 11.5 }}>
              {fecha > todayLocal()
                ? `Esta salida es el ${fecha}. El check-in y el cobro se hacen ese día.`
                : `Esta salida fue el ${fecha} y ya pasó. El día está cerrado.`}
            </Alert>
          )}
          {requierePago && !yaEmbarcado && !yaNoShow && (
            <Tooltip title={esDiaDeSalida ? '' : tooltipNoEsHoy}>
              <span>
                <Button variant="contained" size="large" disableElevation fullWidth
                  startIcon={<PaymentIcon />} onClick={onCobrar}
                  disabled={!esDiaDeSalida}
                  sx={{ py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 15,
                        background: esDiaDeSalida
                          ? 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)'
                          : undefined }}>
                  Cobrar {fmtMoney(montoAcobrarMuelle(reserva))}
                </Button>
              </span>
            </Tooltip>
          )}
          {esCxC && !yaEmbarcado && !yaNoShow && (
            <Alert severity="info" sx={{ fontSize: 12, py: 0.5,
              bgcolor: '#e0f2fe', borderLeft: '4px solid #0891b2',
              '& .MuiAlert-icon': { color: '#0891b2' } }}>
              <strong>CxC agencia — {fmtMoney(reserva.saldo_pendiente)}</strong>
              <br />
              El saldo lo asume <strong>{reserva.agencia_nombre || 'la agencia'}</strong> contra su cupo de crédito.
              No se cobra en muelle — se liquida después desde Finanzas → CxC.
            </Alert>
          )}
          {!requierePago && !yaEmbarcado && !yaNoShow && (
            <Tooltip title={esDiaDeSalida ? '' : tooltipNoEsHoy}>
              <span>
                <Button variant="contained" size="large" disableElevation fullWidth
                  startIcon={<CheckCircleIcon />} onClick={onEmbarcar}
                  disabled={!esDiaDeSalida}
                  sx={{ py: 1.5, textTransform: 'none', fontWeight: 700, fontSize: 15,
                        background: esDiaDeSalida
                          ? 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)'
                          : undefined }}>
                  Hacer check-in
                </Button>
              </span>
            </Tooltip>
          )}
          {yaEmbarcado && (
            <>
              <Alert severity="success" sx={{ fontWeight: 600 }}>
                <CheckCircleIcon sx={{ fontSize: 16, verticalAlign: 'middle', mr: 0.5 }} />
                Embarcado
              </Alert>
              {/* Deshacer check-in: para el caso de embarque por error. Pide
                  confirmación. Solo disponible el mismo día de la salida —
                  fechas pasadas quedan inmutables. */}
              <Tooltip title={esDiaDeSalida
                ? 'Volver a pendiente. Libera el cupo del bote.'
                : 'No se puede deshacer: la fecha de la salida ya pasó.'}>
                <span>
                  <Button variant="outlined" size="small" startIcon={<UndoIcon />}
                    onClick={onReset} disabled={!esDiaDeSalida} fullWidth
                    sx={{ textTransform: 'none', color: '#dc2626', borderColor: '#fecaca' }}>
                    Deshacer check-in
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {yaNoShow && (
            <>
              <Alert severity="info" sx={{ fontWeight: 600 }}>
                Marcado como no-show
              </Alert>
              <Tooltip title={esDiaDeSalida
                ? 'Volver a pendiente.'
                : 'No se puede deshacer: la fecha de la salida ya pasó.'}>
                <span>
                  <Button variant="outlined" size="small" startIcon={<UndoIcon />}
                    onClick={onReset} disabled={!esDiaDeSalida} fullWidth
                    sx={{ textTransform: 'none', color: '#475569', borderColor: '#cbd5e1' }}>
                    Deshacer no-show
                  </Button>
                </span>
              </Tooltip>
            </>
          )}
          {!yaEmbarcado && !yaNoShow && (
            <Tooltip title={esDiaDeSalida ? '' : tooltipNoEsHoy}>
              <span>
                <Button variant="outlined" size="small" startIcon={<EventBusyIcon />}
                  onClick={onNoShow} disabled={!esDiaDeSalida}
                  sx={{ textTransform: 'none', color: '#dc2626', borderColor: '#fecaca' }}>
                  Marcar no-show
                </Button>
              </span>
            </Tooltip>
          )}
        </Box>

      </Box>
    </BottomSheet>
  );
}

function HistorialReserva({ reservaId, fecha }) {
  // Fetch eager (al abrir la reserva ya carga la auditoría) para que el
  // operador vea el conteo y decida si expandir. Si hay eventos, el panel
  // queda abierto por defecto — el caso normal es que el operador SÍ quiere
  // ver el historial cuando abre una reserva.
  const empresaId = getEmpresaId();
  const { data, isLoading } = useQuery({
    queryKey: ['auditoria-reserva', reservaId],
    queryFn: async () => {
      const u = `${API_BASE}/turismo/auditoria?empresa_id=${empresaId}&entidad_tipo=reservacion&entidad_id=${reservaId}&limit=50`;
      const r = await fetch(u, { headers: getAuthHeaders() });
      if (!r.ok) return { items: [] };
      return r.json();
    },
    enabled: !!reservaId,
    staleTime: 30 * 1000,
  });
  const total = data?.total ?? null;
  // Abierto por defecto si hay eventos; el operador puede colapsar.
  const [expandido, setExpandido] = useState(true);
  React.useEffect(() => { if (total === 0) setExpandido(false); }, [total]);
  const items = data?.items || [];

  return (
    <Box sx={{ mt: 2, borderTop: '1px solid #f1f5f9', pt: 1.5 }}>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <Button onClick={() => setExpandido(v => !v)} fullWidth
          startIcon={<HistoryIcon />}
          endIcon={<ExpandMoreIcon sx={{ transform: expandido ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s' }} />}
          sx={{
            justifyContent: 'space-between', textTransform: 'none', fontWeight: 700,
            fontSize: 12, color: '#475569', px: 1, flex: 1,
          }}>
          {expandido ? 'Historial' : `Ver historial${total != null ? ` (${total})` : ''}`}
        </Button>
        {/* Atajo: ver TODO lo que pasó ese día en muelle, no solo de esta reserva. */}
        {fecha && (
          <Tooltip title="Ver día completo en auditoría">
            <IconButton size="small" component="a"
              href={`/trazabilidad?fecha_desde=${fecha}&fecha_hasta=${fecha}`}
              target="_blank" rel="noopener noreferrer"
              sx={{ color: '#94a3b8' }}>
              <OpenInNewIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <Collapse in={expandido} timeout="auto" unmountOnExit>
        <Box sx={{ pt: 1.5 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={18} sx={{ color: '#94a3b8' }} />
            </Box>
          ) : items.length === 0 ? (
            <Typography sx={{ fontSize: 11.5, color: '#94a3b8', textAlign: 'center', py: 2 }}>
              Sin eventos registrados para esta reserva.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {items.map(ev => <EventoTimeline key={ev.id} ev={ev} />)}
            </Box>
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

function EventoTimeline({ ev }) {
  const cuando = ev.created_at ? new Date(ev.created_at) : null;
  const horaLocal = cuando ? cuando.toLocaleString('es-CO', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  }) : '—';
  const accionLabel = (ev.accion || '').replace(/_/g, ' ');
  return (
    <Box sx={{
      p: 1, borderRadius: 1.5, bgcolor: '#f8fafc',
      borderLeft: '3px solid #0d9488',
    }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 1 }}>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: '#0f172a', textTransform: 'capitalize' }}>
          {accionLabel}
        </Typography>
        <Typography sx={{ fontSize: 10, color: '#94a3b8', whiteSpace: 'nowrap' }}>
          {horaLocal}
        </Typography>
      </Box>
      {ev.descripcion && (
        <Typography sx={{ fontSize: 11, color: '#475569', mt: 0.25, lineHeight: 1.35 }}>
          {ev.descripcion}
        </Typography>
      )}
      {(ev.usuario_nombre || ev.usuario_email || ev.origen) && (
        <Typography sx={{ fontSize: 10, color: '#94a3b8', mt: 0.25 }}>
          {ev.usuario_nombre || ev.usuario_email || '—'}{ev.origen ? ` · ${ev.origen}` : ''}
        </Typography>
      )}
    </Box>
  );
}

function InfoTile({ icon, label, value }) {
  return (
    <Box sx={{ p: 1.25, borderRadius: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.25, color: '#94a3b8' }}>
        {React.cloneElement(icon, { sx: { fontSize: 13 } })}
        <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase' }}>{label}</Typography>
      </Box>
      <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a' }} noWrap>{value}</Typography>
    </Box>
  );
}


// Mapeos entre "tipo de cuenta destino" (config del sistema) y método técnico
// que espera el backend. Uniforma la UI: el operador elige una CUENTA, no un
// método abstracto. El método técnico se deriva del tipo.
const TIPO_A_METODO = {
  efectivo: 'efectivo',
  datafono: 'tarjeta',
  bold: 'tarjeta',       // Bold físico funciona como datáfono, sin link
  nequi: 'transferencia',
  bancolombia: 'transferencia',
  banco_bogota: 'transferencia',
  wompi: 'transferencia',
};
const tipoAMetodo = (tipo) => TIPO_A_METODO[tipo] || 'transferencia';
// Tipos que exigen referencia + comprobante (el cash puro no).
const TIPO_REQUIERE_SOPORTE = new Set(['datafono', 'bold', 'nequi', 'bancolombia', 'banco_bogota', 'wompi']);
const TIPO_LABEL = {
  efectivo: 'Efectivo',
  datafono: 'Datáfono',
  bold: 'Bold (datáfono)',
  nequi: 'Nequi',
  bancolombia: 'Bancolombia',
  banco_bogota: 'Banco de Bogotá',
  wompi: 'Wompi',
};
const TIPO_EMOJI = {
  efectivo: '💵', datafono: '💳', bold: '💳',
  nequi: '📱', bancolombia: '🏦', banco_bogota: '🏦', wompi: '🏦',
};

function CobroSheet({ reserva, onClose, onSuccess, onError, onQueued }) {
  const queryClient = useQueryClient();
  // Fuente de verdad: la CUENTA destino elegida. El "método técnico" se deriva
  // del tipo de esa cuenta al momento del submit (ver `resolverMetodoYCuenta`).
  const [cuentaDestinoId, setCuentaDestinoId] = useState('');
  // Guarda el id de la cuenta que Claude Vision detectó (para chip "Detectada por IA").
  // Se limpia cuando el operador cambia de cuenta o abre un nuevo cobro.
  const [cuentaAutoDetectada, setCuentaAutoDetectada] = useState(null);
  const [monto, setMonto] = useState('');
  const [referencia, setReferencia] = useState('');
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);
  // Modo "pago mixto": cliente paga con varios métodos a la vez (ej: parte
  // efectivo + parte tarjeta). Cada línea se aplica como un cobro
  // independiente, todos atómicamente en el backend (cobrar-mixto).
  const [modoMixto, setModoMixto] = useState(false);
  // Cada línea guarda su propio comprobante + estado de subida/OCR — replica
  // la estructura del modo simple, pero por línea. Backend admite
  // `comprobante_url` y `ocr_resultado` por línea (muelle.py:1613+).
  const _LINEA_MIXTA_VACIA = {
    cuentaDestinoId: '',
    monto: '',
    referencia: '',
    comprobanteUrl: '',
    comprobanteNombre: '',
    subiendo: false,
    ocr: null,
    analizandoOCR: false,
  };
  const [lineasMixtas, setLineasMixtas] = useState([
    { ..._LINEA_MIXTA_VACIA },
    { ..._LINEA_MIXTA_VACIA },
  ]);
  // Cuentas destino configuradas por el admin. Se cargan al montar y se usan
  // como opciones del select (reemplaza los 5 métodos hardcodeados).
  const [cuentasDestino, setCuentasDestino] = useState([]);
  // Estado del flujo BOLD: cuando se genera el link, se muestra y se permite compartir.
  const [boldLink, setBoldLink] = useState(null);   // {url, payment_link_id, monto}
  const [generandoBold, setGenerandoBold] = useState(false);
  // Resultado del OCR del comprobante (Claude Vision)
  const [ocr, setOcr] = useState(null);             // {ok, monto, referencia, nivel_alerta, ...}
  const [analizandoOCR, setAnalizandoOCR] = useState(false);

  // Polling del estado del pago Bold (se activa cuando hay link generado).
  // Cada 5s mientras pendiente; se detiene cuando alcanza estado terminal.
  const { data: pagoStatus } = useQuery({
    queryKey: ['pago-bold-status', boldLink?.payment_link_id],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/turismo/reserva-form/estado-pago/${boldLink.payment_link_id}`);
      if (!r.ok) throw new Error('error');
      return r.json();
    },
    enabled: !!boldLink?.payment_link_id,
    refetchInterval: (q) => {
      const e = q?.state?.data?.estado;
      return (e === 'pagado' || e === 'rechazado' || e === 'expirado') ? false : 5000;
    },
  });
  const pagoConfirmado = pagoStatus?.estado === 'pagado';
  const pagoFallido = pagoStatus?.estado === 'rechazado' || pagoStatus?.estado === 'expirado';

  // Cuando el pago Bold se confirma, refrescar la lista del muelle
  useEffect(() => {
    if (pagoConfirmado) {
      queryClient.invalidateQueries({ queryKey: ['muelle'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
    }
  }, [pagoConfirmado, queryClient]);

  // Cargar cuentas destino una sola vez al montar el sheet (o cuando cambia la reserva).
  useEffect(() => {
    let cancel = false;
    fetch(`${API_BASE}/turismo/cuentas-destino-pago?empresa_id=${getEmpresaId()}`, {
      headers: getAuthHeaders(),
    })
      .then(r => r.ok ? r.json() : [])
      .then(list => {
        if (cancel) return;
        const activas = Array.isArray(list) ? list.filter(c => c.activo) : [];
        setCuentasDestino(activas);
        // Default: primera efectivo (o la primera de la lista). Es la más común
        // en muelle y evita empezar con "elegí una cuenta".
        if (activas.length && !cuentaDestinoId) {
          const ef = activas.find(c => c.tipo === 'efectivo') || activas[0];
          setCuentaDestinoId(ef.id);
        }
      })
      .catch(() => { if (!cancel) setCuentasDestino([]); });
    return () => { cancel = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (reserva) {
      // Default del monto: la parte cobrable en muelle (excluye CxC agencia
      // si la reserva es mixta). Bug histórico: cobraba el saldo_pendiente
      // completo (incluyendo la parte de CxC), pidiendo demás al turista.
      setMonto(String(montoAcobrarMuelle(reserva) || ''));
      // Auto-seleccionar efectivo si ya se cargaron las cuentas.
      const ef = cuentasDestino.find(c => c.tipo === 'efectivo') || cuentasDestino[0];
      setCuentaDestinoId(ef ? ef.id : '');
      setReferencia('');
      setComprobanteUrl('');
      setComprobanteNombre('');
      setBoldLink(null);
      setOcr(null);
      setCuentaAutoDetectada(null);
      setModoMixto(false);
      setLineasMixtas([
        { ..._LINEA_MIXTA_VACIA },
        { ..._LINEA_MIXTA_VACIA },
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reserva]);

  // Cuenta actualmente seleccionada (modo simple) y sus derivados
  const cuentaSel = cuentasDestino.find(c => String(c.id) === String(cuentaDestinoId));
  const tipoSel = cuentaSel?.tipo || '';
  const metodo = tipoAMetodo(tipoSel);
  const requiereSoporte = TIPO_REQUIERE_SOPORTE.has(tipoSel);
  const esTarjeta = tipoSel === 'datafono' || tipoSel === 'bold';

  // ── Helpers de modo mixto ────────────────────────────────────────────────
  // Resuelve la cuenta y su tipo desde un id (para validaciones por línea).
  const cuentaPorId = (id) => cuentasDestino.find(c => String(c.id) === String(id));
  const totalMixto = lineasMixtas.reduce((acc, ln) => acc + (Number(ln.monto) || 0), 0);
  const saldoMixto = montoAcobrarMuelle(reserva);
  const diffMixto = saldoMixto - totalMixto;
  const validacionMixta = !modoMixto ? null : (
    lineasMixtas.some(ln => !ln.cuentaDestinoId) ? 'Hay líneas sin cuenta destino'
    : lineasMixtas.some(ln => (Number(ln.monto) || 0) <= 0) ? 'Cada línea debe tener monto > 0'
    : totalMixto > saldoMixto + 0.01 ? `El total excede el saldo (${fmtMoney(saldoMixto)})`
    : lineasMixtas.some(ln => {
        const tipo = cuentaPorId(ln.cuentaDestinoId)?.tipo;
        return tipo && tipo !== 'efectivo' && !(ln.referencia || '').trim();
      }) ? 'Datáfono/Transferencia/Bold/Nequi requieren referencia'
    // Bloque E — exigir comprobante en toda línea que no sea efectivo. Coherente
    // con TIPO_REQUIERE_SOPORTE del modo simple y con el trigger
    // fn_evaluar_voucher_pendiente que marcaría la reserva como pendiente
    // por subsanar si el pago no-efectivo se cierra sin voucher.
    : lineasMixtas.some(ln => {
        const tipo = cuentaPorId(ln.cuentaDestinoId)?.tipo;
        return tipo && TIPO_REQUIERE_SOPORTE.has(tipo) && !ln.comprobanteUrl;
      }) ? 'Cada línea no-efectivo requiere comprobante'
    : lineasMixtas.some(ln => ln.subiendo || ln.analizandoOCR)
      ? 'Esperá a que termine de subir el comprobante'
    : null
  );

  const agregarLinea = () => {
    if (lineasMixtas.length >= 4) return;
    setLineasMixtas([...lineasMixtas, { ..._LINEA_MIXTA_VACIA }]);
  };
  const quitarLinea = (idx) => {
    if (lineasMixtas.length <= 1) return;
    setLineasMixtas(lineasMixtas.filter((_, i) => i !== idx));
  };
  const updateLinea = (idx, campo, valor) => {
    setLineasMixtas(lineasMixtas.map((ln, i) => i === idx ? { ...ln, [campo]: valor } : ln));
  };
  const updateLineaPatch = (idx, patch) => {
    setLineasMixtas((prev) => prev.map((ln, i) => i === idx ? { ...ln, ...patch } : ln));
  };

  // Sube el comprobante de UNA línea y, si aplica, dispara OCR asistido.
  // Réplica del `handleUpload` del modo simple pero indexado por línea.
  const handleUploadLinea = async (idx, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    updateLineaPatch(idx, { subiendo: true, ocr: null });
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd,
      });
      if (!r.ok) throw new Error('Error subiendo');
      const d = await r.json();
      updateLineaPatch(idx, {
        comprobanteUrl: d.url,
        comprobanteNombre: file.name,
        subiendo: false,
      });
      // OCR asistido para tarjeta / transferencia (no bloqueante).
      const tipo = cuentaPorId(lineasMixtas[idx].cuentaDestinoId)?.tipo;
      const metodoLn = tipoAMetodo(tipo);
      if (tipo === 'datafono' || tipo === 'bold' || metodoLn === 'transferencia') {
        analizarLineaIA(idx, d.url);
      }
    } catch (err) {
      updateLineaPatch(idx, { subiendo: false });
      onError(err.message);
    }
  };

  const analizarLineaIA = async (idx, url) => {
    updateLineaPatch(idx, { analizandoOCR: true });
    try {
      const monto_esperado = Number(lineasMixtas[idx]?.monto) || 0;
      const r = await fetch(`${API_BASE}/turismo/soportes/analizar-comprobante`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ url, monto_esperado, empresa_id: getEmpresaId() }),
      });
      const d = await r.json();
      const patch = { ocr: d, analizandoOCR: false };
      // Auto-rellenar referencia si el OCR trae confianza y la línea está vacía
      if (d.ok && d.referencia && !(lineasMixtas[idx].referencia || '').trim()) {
        patch.referencia = d.referencia;
      }
      updateLineaPatch(idx, patch);
    } catch (err) {
      updateLineaPatch(idx, { ocr: { ok: false, error: 'No se pudo analizar' }, analizandoOCR: false });
    }
  };

  if (!reserva) return null;

  const montoNum = Number(monto) || 0;
  const saldoNum = montoAcobrarMuelle(reserva);
  const validacion =
    !cuentaDestinoId ? 'Elegí una cuenta destino'
    : montoNum <= 0 ? 'Monto debe ser mayor a 0'
    : montoNum > saldoNum + 0.01 ? `Excede el saldo (${fmtMoney(saldoNum)})`
    : tipoSel !== 'efectivo' && !referencia.trim() ? 'Indica la referencia'
    : requiereSoporte && !comprobanteUrl ? 'Falta el comprobante'
    : null;

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    setOcr(null);
    try {
      const fd = new FormData();
      fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, {
        method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd,
      });
      if (!r.ok) throw new Error('Error subiendo');
      const d = await r.json();
      setComprobanteUrl(d.url);
      setComprobanteNombre(file.name);
      // Dispara OCR para tarjeta y transferencia (asistencia, no gate)
      if (esTarjeta || metodo === 'transferencia') {
        analizarComprobanteIA(d.url);
      }
    } catch (err) { onError(err.message); }
    setSubiendo(false);
  };

  const analizarComprobanteIA = async (url) => {
    setAnalizandoOCR(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/soportes/analizar-comprobante`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ url, monto_esperado: montoNum, empresa_id: getEmpresaId() }),
      });
      const d = await r.json();
      setOcr(d);
      // Prellenar referencia solo si está vacía y el OCR tiene confianza
      if (d.ok && d.referencia && !referencia.trim()) {
        setReferencia(d.referencia);
      }
      // Auto-seleccionar cuenta destino si el backend hizo match único y
      // esa cuenta está en las cuentas activas de la empresa. El operador
      // sigue pudiendo cambiarla (las cards son clickeables). Marcamos
      // `cuentaAutoDetectada` para mostrar el chip "Detectada por IA".
      const match = d?.cuenta_destino_match;
      if (match?.cuenta_id && cuentasDestino.some(c => c.id === match.cuenta_id)) {
        setCuentaDestinoId(match.cuenta_id);
        setCuentaAutoDetectada(match.cuenta_id);
      }
    } catch (err) {
      setOcr({ ok: false, error: 'No se pudo analizar' });
    }
    setAnalizandoOCR(false);
  };

  const handleSubmit = async () => {
    // Modo mixto: llama al endpoint cobrar-mixto con todas las líneas atómicamente.
    if (modoMixto) {
      if (validacionMixta) return onError(validacionMixta);
      setSaving(true);
      const body = {
        lineas: lineasMixtas.map(ln => {
          const c = cuentaPorId(ln.cuentaDestinoId);
          return {
            metodo_pago: tipoAMetodo(c?.tipo),
            monto: Number(ln.monto),
            referencia: (ln.referencia || '').trim() || null,
            cuenta_destino_id: c ? c.id : null,
            // Bloque D — el backend acepta comprobante_url y ocr_resultado
            // por línea; sin esto los pagos no-efectivo del mixto quedaban
            // sin voucher y disparaban voucher_pendiente_subsanar.
            comprobante_url: ln.comprobanteUrl || null,
            ocr_resultado: ln.ocr || null,
          };
        }),
        cobrado_por_user_id: getUserId(),
        cobrado_por_nombre: getUserName(),
        expected_version: reserva.version,
      };
      try {
        if (!isOnline()) {
          await queueMutation({
            url: `${API_BASE}/turismo/muelle/reservas/${reserva.id}/cobrar-mixto`,
            method: 'POST', body, label: `Cobro mixto ${reserva.codigo_reserva}`,
          });
          onQueued();
          return;
        }
        const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${reserva.id}/cobrar-mixto`, {
          method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
        });
        if (!r.ok) {
          const err = await r.json().catch(()=>({}));
          const e = new Error(err.detail || 'Error');
          e.status = r.status;
          throw e;
        }
        onSuccess();
      } catch (e) { onError(e); }
      setSaving(false);
      return;
    }

    // Modo simple (un solo método): lógica original.
    if (validacion) return onError(validacion);
    setSaving(true);
    const body = {
      metodo_pago: metodo,
      monto: montoNum,
      referencia: referencia.trim() || null,
      comprobante_url: comprobanteUrl || null,
      cobrado_por_user_id: getUserId(),
      cobrado_por_nombre: getUserName(),
      ocr_resultado: ocr && ocr.ok ? ocr : null,
      cuenta_destino_id: cuentaSel ? cuentaSel.id : null,
      // OCC — la reserva.version viene del payload de /muelle/dia.
      expected_version: reserva.version,
    };
    try {
      if (!isOnline()) {
        await queueMutation({
          url: `${API_BASE}/turismo/muelle/reservas/${reserva.id}/cobrar`,
          method: 'PATCH', body, label: `Cobro ${reserva.codigo_reserva}`,
        });
        onQueued();
        return;
      }
      const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${reserva.id}/cobrar`, {
        method: 'PATCH', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) {
        const err = await r.json().catch(()=>({}));
        const e = new Error(err.detail || 'Error');
        e.status = r.status;
        throw e;
      }
      onSuccess();
    } catch (e) {
      // Pasar el Error completo para que el caller pueda detectar 409 y
      // disparar el flujo de "datos actualizados — refrescá".
      onError(e);
    }
    setSaving(false);
  };

  // Para bold_link, sustituimos el flujo de cobro directo por "generar link + compartir".
  // El cobro queda pendiente hasta que Bold notifique vía webhook.
  const handleGenerarBold = async () => {
    setGenerandoBold(true);
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/reservas/${reserva.id}/generar-link-bold`, {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ monto: montoNum }),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      const d = await r.json();
      setBoldLink(d);
    } catch (e) { onError(e.message); }
    setGenerandoBold(false);
  };

  const compartirWhatsApp = () => {
    if (!boldLink) return;
    const tel = (reserva.telefono || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Link de pago para tu reserva ${reserva.codigo_reserva}:\n${boldLink.url}\n\nMonto: ${fmtMoney(boldLink.monto)}`
    );
    const wa = tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(wa, '_blank');
  };

  const esBold = metodo === 'bold_link';

  return (
    <BottomSheet open={!!reserva} onClose={onClose}
      title={esBold && boldLink ? (
        pagoConfirmado ? '✓ Pago exitoso'
        : pagoFallido ? (pagoStatus.estado === 'rechazado' ? 'Pago rechazado' : 'Link expirado')
        : 'Link BOLD generado'
      ) : `Cobrar ${fmtMoney(montoAcobrarMuelle(reserva))}`}
      footer={
        esBold && boldLink ? (
          pagoConfirmado || pagoFallido ? (
            // Pagos terminales: solo botón Cerrar
            <Button fullWidth variant="contained" disableElevation
              onClick={() => { setBoldLink(null); onClose(); }}
              sx={{ textTransform: 'none', fontWeight: 700,
                     background: pagoConfirmado
                       ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                       : '#64748b' }}>
              Cerrar
            </Button>
          ) : (
            // Pendiente: los 3 botones de siempre
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button fullWidth variant="contained" disableElevation
                onClick={() => window.open(boldLink.url, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', fontWeight: 700,
                       background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                💳 Pagar ahora
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth onClick={onClose}
                  sx={{ textTransform: 'none', color: '#64748b' }}>Cerrar</Button>
                <Button fullWidth variant="contained" disableElevation onClick={compartirWhatsApp}
                  sx={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  Enviar por WhatsApp
                </Button>
              </Box>
            </Box>
          )
        ) : esBold ? (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button fullWidth onClick={onClose} disabled={generandoBold}
              sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
            <Button fullWidth variant="contained" disableElevation onClick={handleGenerarBold}
              disabled={generandoBold || montoNum <= 0 || montoNum > saldoNum}
              sx={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
              {generandoBold ? 'Generando…' : 'Generar link BOLD'}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button fullWidth onClick={onClose} disabled={saving}
              sx={{ textTransform: 'none', color: '#64748b' }}>Cancelar</Button>
            <Button fullWidth variant="contained" disableElevation onClick={handleSubmit}
              disabled={(modoMixto ? !!validacionMixta : !!validacion) || saving}
              sx={{ textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
              {saving ? 'Cobrando…' : 'Confirmar cobro'}
            </Button>
          </Box>
        )
      }>
      <Box sx={{ p: 2 }}>
        {/* Vista post-generación: link BOLD con estado dinámico */}
        {esBold && boldLink ? (
          <Box>
            {pagoConfirmado ? (
              // PAGADO — pantalla verde de éxito
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15803d', mb: 0.5 }}>
                  Pago confirmado
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
                  La reserva está lista para embarcar.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 2 }}>
                  <InfoTile icon={<PaymentIcon />} label="Monto" value={fmtMoney(pagoStatus?.monto || boldLink.monto)} />
                  <InfoTile icon={<PersonIcon />} label="Cliente" value={formatNombre(reserva.nombre_cliente)} />
                </Box>
                {pagoStatus?.payment_method && (
                  <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1.5 }}>
                    Método: <strong>{pagoStatus.payment_method}</strong>
                    {pagoStatus.transaction_id && <> · TXN: <code style={{ fontSize: 10 }}>{pagoStatus.transaction_id}</code></>}
                  </Typography>
                )}
              </Box>
            ) : pagoFallido ? (
              // RECHAZADO o EXPIRADO
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: 56, mb: 1 }}>
                  {pagoStatus.estado === 'rechazado' ? '⛔' : '⌛'}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#991b1b', mb: 0.5 }}>
                  {pagoStatus.estado === 'rechazado' ? 'Pago rechazado' : 'Link expirado'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mb: 2 }}>
                  {pagoStatus.estado === 'rechazado'
                    ? 'El pago no se completó. Genera un nuevo link.'
                    : 'Este link ya no es válido. Genera uno nuevo.'}
                </Typography>
              </Box>
            ) : (
              // PENDIENTE — pantalla normal con URL + botones
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Link BOLD listo. Usa <strong>Pagar ahora</strong> si el cliente está presente
                  y paga aquí, o <strong>Enviar por WhatsApp</strong> para que pague desde su celular.
                </Alert>
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>URL</Typography>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', color: '#0f172a', my: 0.5 }}>
                    {boldLink.url}
                  </Typography>
                  <Button size="small" onClick={() => { navigator.clipboard?.writeText(boldLink.url); }}
                    sx={{ textTransform: 'none', mt: 0.5 }}>Copiar URL</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <InfoTile icon={<PaymentIcon />} label="Monto" value={fmtMoney(boldLink.monto)} />
                  <InfoTile icon={<PersonIcon />} label="Cliente" value={formatNombre(reserva.nombre_cliente)} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'center' }}>
                  <CircularProgress size={12} sx={{ color: '#0d9488' }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    Esperando confirmación de pago…
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        ) : (
        <>
        <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>
          {formatNombre(reserva.nombre_cliente)} · {reserva.codigo_reserva}
        </Typography>

        {/* Toggle Pago mixto — cliente paga con varios métodos en simultáneo */}
        <Box sx={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          p: 1.25, borderRadius: 2,
          bgcolor: modoMixto ? '#f0fdfa' : '#f8fafc',
          border: `1px solid ${modoMixto ? '#0d9488' : '#e2e8f0'}`,
          mb: 1.5,
        }}>
          <Box>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
              Pago mixto
            </Typography>
            <Typography sx={{ fontSize: 10, color: '#64748b' }}>
              Cliente paga con varios métodos a la vez (ej: parte efectivo + parte tarjeta).
            </Typography>
          </Box>
          <Box onClick={() => setModoMixto(!modoMixto)}
            sx={{
              width: 40, height: 22, borderRadius: 11, cursor: 'pointer', position: 'relative',
              bgcolor: modoMixto ? '#0d9488' : '#cbd5e1', transition: 'background 0.15s',
            }}>
            <Box sx={{
              position: 'absolute', top: 2, left: modoMixto ? 20 : 2,
              width: 18, height: 18, borderRadius: '50%', bgcolor: '#fff',
              transition: 'left 0.15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
            }} />
          </Box>
        </Box>

        {!modoMixto && (
        <>
        <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}>
          Cuenta destino
        </Typography>
        {cuentasDestino.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2, fontSize: 12 }}>
            Cargando cuentas destino…
          </Alert>
        ) : (
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1, mb: 2 }}>
            {cuentasDestino.map(c => {
              const activa = String(cuentaDestinoId) === String(c.id);
              const detectadaPorIA = String(cuentaAutoDetectada) === String(c.id);
              return (
                <Box key={c.id}
                  onClick={() => {
                    setCuentaDestinoId(c.id);
                    // Si el operador elige distinto a lo detectado, quitamos el chip
                    if (String(cuentaAutoDetectada) !== String(c.id)) {
                      setCuentaAutoDetectada(null);
                    }
                  }}
                  sx={{
                    p: 1.25, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                    position: 'relative',
                    border: '2px solid',
                    borderColor: activa
                      ? (detectadaPorIA ? '#8b5cf6' : '#0d9488')
                      : '#e2e8f0',
                    bgcolor: activa
                      ? (detectadaPorIA ? '#f5f3ff' : '#f0fdfa')
                      : '#fff',
                    transition: 'all 0.15s',
                  }}>
                  {detectadaPorIA && (
                    <Box sx={{
                      position: 'absolute', top: -8, left: '50%', transform: 'translateX(-50%)',
                      bgcolor: '#8b5cf6', color: '#fff',
                      px: 0.75, py: 0.15, borderRadius: 1,
                      fontSize: 8.5, fontWeight: 800, letterSpacing: '0.05em',
                      whiteSpace: 'nowrap',
                      boxShadow: '0 2px 6px rgba(139, 92, 246, 0.4)',
                    }}>
                      ✨ DETECTADA POR IA
                    </Box>
                  )}
                  <Typography sx={{ fontSize: 20, lineHeight: 1 }}>
                    {TIPO_EMOJI[c.tipo] || '🏦'}
                  </Typography>
                  <Typography sx={{ fontSize: 11, fontWeight: 700,
                    color: activa ? (detectadaPorIA ? '#7c3aed' : '#0d9488') : '#0f172a', mt: 0.5 }}>
                    {c.nombre}
                  </Typography>
                  <Typography sx={{ fontSize: 9, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {TIPO_LABEL[c.tipo] || c.tipo}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
        </>
        )}

        {!modoMixto && (
        <>
        <TextField fullWidth size="small" type="number" label="Monto a cobrar"
          value={monto} onChange={e => setMonto(e.target.value)}
          inputProps={{ min: 0, max: saldoNum }}
          helperText={`Saldo: ${fmtMoney(saldoNum)}`}
          sx={{ mb: 2 }} />

        {requiereSoporte && (
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
            <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}>
              Soporte del pago
            </Typography>
            <TextField fullWidth size="small" label={metodo === 'transferencia' ? 'Referencia *' : 'Referencia / autorización'}
              value={referencia} onChange={e => setReferencia(e.target.value)} sx={{ mb: 1 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button component="label" variant="outlined" size="small" disabled={subiendo}
                startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                sx={{ textTransform: 'none' }}>
                {subiendo ? 'Subiendo…' : 'Comprobante'}
                <input hidden type="file" accept=".pdf,image/*" onChange={handleUpload} />
              </Button>
              {comprobanteUrl && (
                <Typography sx={{ fontSize: 11, color: '#16a34a', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis' }} noWrap>
                  ✓ {comprobanteNombre}
                </Typography>
              )}
            </Box>

            {/* Resultado del OCR — asistente, nunca bloqueante */}
            <OCRPanel
              loading={analizandoOCR}
              result={ocr}
              esAplicable={esTarjeta || metodo === 'transferencia'}
            />
          </Box>
        )}

        {validacion && !esBold && <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12 }}>{validacion}</Alert>}
        </>
        )}

        {/* ── MODO PAGO MIXTO ────────────────────────────────────────────── */}
        {modoMixto && (
          <Box>
            <Typography sx={{ fontSize: 11, color: '#475569', fontWeight: 700, mb: 1, textTransform: 'uppercase' }}>
              Líneas de pago
            </Typography>
            <Stack spacing={1.25}>
              {lineasMixtas.map((ln, idx) => {
                const cta = cuentaPorId(ln.cuentaDestinoId);
                const requiereRef = cta && cta.tipo !== 'efectivo';
                const requiereSoporteLn = cta && TIPO_REQUIERE_SOPORTE.has(cta.tipo);
                const metodoLn = tipoAMetodo(cta?.tipo);
                const esTarjetaLn = cta?.tipo === 'datafono' || cta?.tipo === 'bold';
                return (
                  <Box key={idx} sx={{ p: 1.25, borderRadius: 2, bgcolor: '#f8fafc', border: '1px solid #e2e8f0' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                      <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 700 }}>
                        Línea {idx + 1}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {lineasMixtas.length > 1 && (
                        <Button size="small" onClick={() => quitarLinea(idx)}
                          sx={{ textTransform: 'none', fontSize: 11, color: '#dc2626', minWidth: 0 }}>
                          ✕ Quitar
                        </Button>
                      )}
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: requiereRef ? 1 : 0 }}>
                      <TextField select size="small" label="Cuenta destino"
                        value={ln.cuentaDestinoId}
                        onChange={e => updateLinea(idx, 'cuentaDestinoId', e.target.value)}>
                        {cuentasDestino.map(c => (
                          <MenuItem key={c.id} value={c.id}>
                            {(TIPO_EMOJI[c.tipo] || '🏦')} {c.nombre}
                          </MenuItem>
                        ))}
                      </TextField>
                      <TextField size="small" type="number" label="Monto" value={ln.monto}
                        onChange={e => updateLinea(idx, 'monto', e.target.value)}
                        inputProps={{ min: 0, step: 1000 }} />
                    </Box>
                    {requiereRef && (
                      <TextField fullWidth size="small" label="Referencia / autorización *"
                        value={ln.referencia}
                        onChange={e => updateLinea(idx, 'referencia', e.target.value)}
                        sx={{ mb: requiereSoporteLn ? 1 : 0 }} />
                    )}
                    {/* Bloque C — uploader de comprobante por línea. Solo visible
                        para tipos que requieren soporte (datáfono, transferencia,
                        bold, nequi). Replica el patrón del modo simple. */}
                    {requiereSoporteLn && (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Button component="label" variant="outlined" size="small"
                            disabled={ln.subiendo}
                            startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />}
                            sx={{ textTransform: 'none' }}>
                            {ln.subiendo ? 'Subiendo…' : (ln.comprobanteUrl ? 'Cambiar comprobante' : 'Comprobante *')}
                            <input hidden type="file" accept=".pdf,image/*"
                              onChange={(e) => handleUploadLinea(idx, e)} />
                          </Button>
                          {ln.comprobanteUrl && (
                            <Typography sx={{
                              fontSize: 11, color: '#16a34a',
                              flex: 1, overflow: 'hidden', textOverflow: 'ellipsis',
                            }} noWrap>
                              ✓ {ln.comprobanteNombre}
                            </Typography>
                          )}
                        </Box>
                        <OCRPanel
                          loading={ln.analizandoOCR}
                          result={ln.ocr}
                          esAplicable={esTarjetaLn || metodoLn === 'transferencia'}
                        />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Stack>
            {lineasMixtas.length < 4 && (
              <Button size="small" onClick={agregarLinea}
                sx={{ textTransform: 'none', mt: 1, color: '#0d9488' }}>
                + Agregar línea
              </Button>
            )}

            {/* Resumen del cobro mixto */}
            <Box sx={{
              mt: 2, p: 1.25, borderRadius: 2,
              bgcolor: Math.abs(diffMixto) < 0.5 ? '#dcfce7' : (totalMixto > saldoMixto ? '#fee2e2' : '#fef3c7'),
              border: `1px solid ${Math.abs(diffMixto) < 0.5 ? '#16a34a' : (totalMixto > saldoMixto ? '#dc2626' : '#f59e0b')}`,
            }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: '#475569' }}>Saldo a cobrar</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{fmtMoney(saldoMixto)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                <Typography sx={{ fontSize: 11, color: '#475569' }}>Total distribuido</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0d9488' }}>{fmtMoney(totalMixto)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography sx={{ fontSize: 11, color: '#475569' }}>Diferencia</Typography>
                <Typography sx={{ fontSize: 13, fontWeight: 700,
                  color: Math.abs(diffMixto) < 0.5 ? '#16a34a' : (totalMixto > saldoMixto ? '#dc2626' : '#f59e0b') }}>
                  {Math.abs(diffMixto) < 0.5 ? '✓ Cuadra'
                   : totalMixto > saldoMixto ? `excede en ${fmtMoney(Math.abs(diffMixto))}`
                   : `faltan ${fmtMoney(Math.abs(diffMixto))}`}
                </Typography>
              </Box>
            </Box>

            {validacionMixta && <Alert severity="warning" sx={{ mt: 1.5, fontSize: 12 }}>{validacionMixta}</Alert>}
          </Box>
        )}
        </>
        )}
      </Box>
    </BottomSheet>
  );
}

function WalkInSheet({ open, salidas, onClose, onSuccess, onError }) {
  const queryClient = useQueryClient();
  const [step, setStep] = useState(0);
  const [salidaId, setSalidaId] = useState('');
  const [nombre, setNombre] = useState('');
  const [telefono, setTelefono] = useState('');
  const [adultos, setAdultos] = useState(1);
  const [ninos, setNinos] = useState(0);
  const [bebes, setBebes] = useState(0);
  const [metodo, setMetodo] = useState('efectivo');
  const [montoCobrado, setMontoCobrado] = useState('');
  const [referencia, setReferencia] = useState('');
  const [comprobanteUrl, setComprobanteUrl] = useState('');
  const [comprobanteNombre, setComprobanteNombre] = useState('');
  const [subiendo, setSubiendo] = useState(false);
  const [saving, setSaving] = useState(false);
  // Si la venta es Bold, el backend devuelve el link generado y mostramos una
  // pantalla intermedia con "Enviar por WhatsApp" antes de cerrar.
  const [boldResultado, setBoldResultado] = useState(null);
  // OCR del comprobante (asistente, no gate)
  const [ocr, setOcr] = useState(null);
  const [analizandoOCR, setAnalizandoOCR] = useState(false);

  // Polling del estado del pago Bold (cuando el walk-in es con bold_link)
  const { data: pagoStatus } = useQuery({
    queryKey: ['pago-bold-status', boldResultado?.payment_link_id],
    queryFn: async () => {
      const r = await fetch(`${API_BASE}/turismo/reserva-form/estado-pago/${boldResultado.payment_link_id}`);
      if (!r.ok) throw new Error('error');
      return r.json();
    },
    enabled: !!boldResultado?.payment_link_id,
    refetchInterval: (q) => {
      const e = q?.state?.data?.estado;
      return (e === 'pagado' || e === 'rechazado' || e === 'expirado') ? false : 5000;
    },
  });
  const pagoConfirmado = pagoStatus?.estado === 'pagado';
  const pagoFallido = pagoStatus?.estado === 'rechazado' || pagoStatus?.estado === 'expirado';

  useEffect(() => {
    if (pagoConfirmado) {
      queryClient.invalidateQueries({ queryKey: ['muelle'] });
      queryClient.invalidateQueries({ queryKey: ['booking'] });
    }
  }, [pagoConfirmado, queryClient]);

  useEffect(() => {
    if (open) {
      setStep(0); setSalidaId(''); setNombre(''); setTelefono('');
      setAdultos(1); setNinos(0); setBebes(0);
      setMetodo('efectivo'); setMontoCobrado(''); setReferencia('');
      setComprobanteUrl(''); setComprobanteNombre('');
      setBoldResultado(null);
      setOcr(null);
    }
  }, [open]);

  const esBold = metodo === 'bold_link';
  const ocrAplica = metodo === 'tarjeta_credito' || metodo === 'tarjeta_debito' || metodo === 'transferencia';

  const salidaSel = salidas.find(s => s.salida_id === parseInt(salidaId));
  const cuposDisp = salidaSel ? Math.max((salidaSel.total_pax ? 999 : 999), 0) : 0;  // estimación; backend valida real

  // Precio estimado: salidaSel no tiene precio. Lo dejamos para backend.

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setSubiendo(true);
    setOcr(null);
    try {
      const fd = new FormData(); fd.append('archivo', file);
      const t = localStorage.getItem('token');
      const r = await fetch(`${API_BASE}/turismo/soportes/upload`, { method: 'POST', headers: t ? { Authorization: `Bearer ${t}` } : {}, body: fd });
      const d = await r.json();
      setComprobanteUrl(d.url); setComprobanteNombre(file.name);
      // OCR: solo aplica para tarjeta/transferencia
      if (ocrAplica) {
        setAnalizandoOCR(true);
        try {
          const expected = montoCobrado ? Number(montoCobrado) : 0;
          const rr = await fetch(`${API_BASE}/turismo/soportes/analizar-comprobante`, {
            method: 'POST', headers: getAuthHeaders(),
            body: JSON.stringify({ url: d.url, monto_esperado: expected, empresa_id: getEmpresaId() }),
          });
          const oc = await rr.json();
          setOcr(oc);
          if (oc.ok && oc.referencia && !referencia.trim()) setReferencia(oc.referencia);
        } catch { setOcr({ ok: false }); }
        setAnalizandoOCR(false);
      }
    } catch (err) { onError('Error subiendo'); }
    setSubiendo(false);
  };

  const validar = () => {
    if (step === 0 && !salidaId) return 'Selecciona la salida';
    if (step === 1 && (!nombre.trim() || !telefono.trim() || (adultos + ninos) <= 0)) return 'Completa nombre, teléfono y al menos 1 pax';
    return null;
  };

  const handleSubmit = async () => {
    setSaving(true);
    const body = {
      salida_id: parseInt(salidaId),
      nombre_cliente: nombre.trim(),
      numero_whatsapp: telefono.trim(),
      adultos: parseInt(adultos) || 1,
      ninos: parseInt(ninos) || 0,
      bebes: parseInt(bebes) || 0,
      metodo_pago: metodo,
      monto_cobrado: montoCobrado ? Number(montoCobrado) : null,
      referencia_pago: referencia.trim() || null,
      comprobante_url: comprobanteUrl || null,
      ocr_resultado: ocr && ocr.ok ? ocr : null,
      // Bold: NO embarcar al crear; el check-in se habilita cuando el webhook
      // confirme el pago y baje el saldo a cero.
      checkin_inmediato: !esBold,
      cobrado_por_user_id: getUserId(),
    };
    try {
      const r = await fetch(`${API_BASE}/turismo/muelle/walkin`, {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(body),
      });
      if (!r.ok) { const e = await r.json().catch(()=>({})); throw new Error(e.detail || 'Error'); }
      const d = await r.json();
      if (esBold && d.payment_url) {
        // Mostrar pantalla intermedia para que el operador envíe el link.
        setBoldResultado(d);
      } else {
        onSuccess();
      }
    } catch (e) { onError(e.message); }
    setSaving(false);
  };

  const compartirWaWalkin = () => {
    if (!boldResultado) return;
    const tel = (telefono || '').replace(/\D/g, '');
    const msg = encodeURIComponent(
      `Hola ${nombre}, este es el link de pago de tu reserva ${boldResultado.codigo_reserva}:\n${boldResultado.payment_url}\n\nMonto: ${fmtMoney(boldResultado.precio_total)}\n\nUna vez completes el pago podrás abordar.`
    );
    const wa = tel ? `https://wa.me/${tel}?text=${msg}` : `https://wa.me/?text=${msg}`;
    window.open(wa, '_blank');
  };

  return (
    <BottomSheet open={open} onClose={onClose}
      title={boldResultado ? (
        pagoConfirmado ? '✓ Pago exitoso'
        : pagoFallido ? (pagoStatus.estado === 'rechazado' ? 'Pago rechazado' : 'Link expirado')
        : 'Link de pago listo'
      ) : `Walk-in en muelle · paso ${step + 1}/3`}
      footer={
        boldResultado ? (
          pagoConfirmado || pagoFallido ? (
            // Terminal: cerrar
            <Button fullWidth variant="contained" disableElevation
              onClick={() => { setBoldResultado(null); onSuccess(); }}
              sx={{ textTransform: 'none', fontWeight: 700,
                     background: pagoConfirmado
                       ? 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)'
                       : '#64748b' }}>
              Cerrar
            </Button>
          ) : (
            // Pendiente: 3 botones
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              <Button fullWidth variant="contained" disableElevation
                onClick={() => window.open(boldResultado.payment_url, '_blank', 'noopener,noreferrer')}
                sx={{ textTransform: 'none', fontWeight: 700,
                       background: 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)' }}>
                💳 Pagar ahora
              </Button>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button fullWidth onClick={() => { setBoldResultado(null); onSuccess(); }}
                  sx={{ textTransform: 'none', color: '#64748b' }}>Cerrar</Button>
                <Button fullWidth variant="contained" disableElevation onClick={compartirWaWalkin}
                  sx={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)' }}>
                  Enviar por WhatsApp
                </Button>
              </Box>
            </Box>
          )
        ) : (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {step > 0 && <Button fullWidth onClick={() => setStep(step - 1)} disabled={saving} sx={{ textTransform: 'none', color: '#64748b' }}>Atrás</Button>}
            {step < 2 ? (
              <Button fullWidth variant="contained" disableElevation
                onClick={() => { const v = validar(); if (v) return onError(v); setStep(step + 1); }}
                sx={{ textTransform: 'none', fontWeight: 700, background: 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                Siguiente
              </Button>
            ) : (
              <Button fullWidth variant="contained" disableElevation onClick={handleSubmit} disabled={saving}
                sx={{ textTransform: 'none', fontWeight: 700,
                       background: esBold
                         ? 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)'
                         : 'linear-gradient(135deg, #0d9488 0%, #14b8a6 100%)' }}>
                {saving ? 'Creando…' : (esBold ? 'Crear y generar link BOLD' : 'Crear y embarcar')}
              </Button>
            )}
          </Box>
        )
      }>
      <Box sx={{ p: 2 }}>
        {/* PANTALLA DE ÉXITO BOLD — sustituye el wizard cuando ya se generó el link */}
        {boldResultado ? (
          <Box>
            {pagoConfirmado ? (
              // PAGADO — pantalla verde
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <CheckCircleIcon sx={{ fontSize: 56, color: '#16a34a', mb: 1 }} />
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#15803d', mb: 0.5 }}>
                  Pago confirmado
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b', mb: 1 }}>
                  Walk-in <strong>{boldResultado.codigo_reserva}</strong> listo para embarcar.
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mt: 2 }}>
                  <InfoTile icon={<PaymentIcon />} label="Monto" value={fmtMoney(pagoStatus?.monto || boldResultado.precio_total)} />
                  <InfoTile icon={<PersonIcon />} label="Cliente" value={nombre} />
                </Box>
                {pagoStatus?.payment_method && (
                  <Typography sx={{ fontSize: 11, color: '#64748b', mt: 1.5 }}>
                    Método: <strong>{pagoStatus.payment_method}</strong>
                    {pagoStatus.transaction_id && <> · TXN: <code style={{ fontSize: 10 }}>{pagoStatus.transaction_id}</code></>}
                  </Typography>
                )}
              </Box>
            ) : pagoFallido ? (
              <Box sx={{ textAlign: 'center', py: 2 }}>
                <Typography sx={{ fontSize: 56, mb: 1 }}>
                  {pagoStatus.estado === 'rechazado' ? '⛔' : '⌛'}
                </Typography>
                <Typography sx={{ fontSize: 18, fontWeight: 800, color: '#991b1b', mb: 0.5 }}>
                  {pagoStatus.estado === 'rechazado' ? 'Pago rechazado' : 'Link expirado'}
                </Typography>
                <Typography sx={{ fontSize: 12, color: '#64748b' }}>
                  Walk-in <strong>{boldResultado.codigo_reserva}</strong> queda sin pago.
                  Genera un nuevo cobro desde la reserva.
                </Typography>
              </Box>
            ) : (
              <>
                <Alert severity="success" sx={{ mb: 2 }}>
                  Walk-in <strong>{boldResultado.codigo_reserva}</strong> creado.<br/>
                  Usa <strong>Pagar ahora</strong> si el cliente está aquí y va a pagar al instante,
                  o <strong>Enviar por WhatsApp</strong> para que pague desde su celular.
                </Alert>
                <Box sx={{ p: 1.5, bgcolor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 2, mb: 2 }}>
                  <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 700, textTransform: 'uppercase' }}>Link de pago</Typography>
                  <Typography sx={{ fontSize: 12, fontFamily: 'monospace', wordBreak: 'break-all', color: '#0f172a', my: 0.5 }}>
                    {boldResultado.payment_url}
                  </Typography>
                  <Button size="small" onClick={() => { navigator.clipboard?.writeText(boldResultado.payment_url); }}
                    sx={{ textTransform: 'none', mt: 0.5 }}>Copiar URL</Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                  <InfoTile icon={<PaymentIcon />} label="Monto" value={fmtMoney(boldResultado.precio_total)} />
                  <InfoTile icon={<PersonIcon />} label="Cliente" value={nombre} />
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5, justifyContent: 'center' }}>
                  <CircularProgress size={12} sx={{ color: '#0d9488' }} />
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    Esperando confirmación de pago…
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        ) : (<>
        {/* PASO 1: Salida */}
        {step === 0 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569', mb: 0.5 }}>Elige la salida</Typography>
            {salidas.length === 0
              ? <Alert severity="info">No hay salidas hoy.</Alert>
              : salidas.map(s => (
                <Box key={s.salida_id} onClick={() => setSalidaId(String(s.salida_id))}
                  sx={{
                    p: 1.5, borderRadius: 2, cursor: 'pointer',
                    border: '2px solid', borderColor: salidaId === String(s.salida_id) ? '#0d9488' : '#e2e8f0',
                    bgcolor: salidaId === String(s.salida_id) ? '#f0fdfa' : '#fff',
                  }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{s.paquete_nombre}</Typography>
                  <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                    {s.hora_salida ? s.hora_salida.slice(0, 5) : '—'} · {s.total_reservas} reservas · {s.embarcados} embarcados
                  </Typography>
                </Box>
              ))}
          </Box>
        )}

        {/* PASO 2: Cliente + pax */}
        {step === 1 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <TextField size="small" label="Nombre completo *" value={nombre} onChange={e => setNombre(e.target.value)} />
            <TextField size="small" label="Teléfono *" type="tel" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="+57…" />
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 1 }}>
              <TextField size="small" type="number" label="Adultos" value={adultos} onChange={e => setAdultos(e.target.value)} inputProps={{ min: 0 }} />
              <TextField size="small" type="number" label="Niños" value={ninos} onChange={e => setNinos(e.target.value)} inputProps={{ min: 0 }} />
              <TextField size="small" type="number" label="Bebés" value={bebes} onChange={e => setBebes(e.target.value)} inputProps={{ min: 0 }} />
            </Box>
          </Box>
        )}

        {/* PASO 3: Cobro */}
        {step === 2 && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569' }}>Cobro</Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
              {[
                { v: 'efectivo', label: 'Efectivo', emoji: '💵' },
                { v: 'transferencia', label: 'Transferencia', emoji: '🏦' },
                { v: 'tarjeta_credito', label: 'T. Crédito', emoji: '💳' },
                { v: 'tarjeta_debito', label: 'T. Débito', emoji: '💳' },
                { v: 'bold_link', label: 'Link BOLD', emoji: '🔗' },
              ].map(m => (
                <Box key={m.v} onClick={() => setMetodo(m.v)}
                  sx={{
                    p: 1, borderRadius: 2, cursor: 'pointer', textAlign: 'center',
                    border: '2px solid', borderColor: metodo === m.v ? '#0d9488' : '#e2e8f0',
                    bgcolor: metodo === m.v ? '#f0fdfa' : '#fff',
                  }}>
                  <Typography sx={{ fontSize: 18 }}>{m.emoji}</Typography>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: metodo === m.v ? '#0d9488' : '#475569' }}>{m.label}</Typography>
                </Box>
              ))}
            </Box>
            <TextField size="small" type="number" label="Monto cobrado (vacío = precio completo)"
              value={montoCobrado} onChange={e => setMontoCobrado(e.target.value)}
              helperText="El backend calcula el precio según la salida." />
            {['transferencia', 'tarjeta', 'bold_link'].includes(metodo) && (
              <>
                <TextField size="small" label="Referencia"
                  value={referencia} onChange={e => setReferencia(e.target.value)} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Button component="label" variant="outlined" size="small" disabled={subiendo}
                    startIcon={<CloudUploadIcon sx={{ fontSize: 16 }} />} sx={{ textTransform: 'none' }}>
                    {subiendo ? 'Subiendo…' : 'Comprobante'}
                    <input hidden type="file" accept=".pdf,image/*" onChange={handleUpload} />
                  </Button>
                  {comprobanteUrl && <Typography sx={{ fontSize: 11, color: '#16a34a' }} noWrap>✓ {comprobanteNombre}</Typography>}
                </Box>
                <OCRPanel loading={analizandoOCR} result={ocr} esAplicable={ocrAplica} />
              </>
            )}
          </Box>
        )}
        </>)}
      </Box>
    </BottomSheet>
  );
}

function SkeletonScreen() {
  return (
    <Box sx={{ p: 2 }}>
      <Skeleton variant="text" width="40%" height={28} />
      <Skeleton variant="rounded" height={40} sx={{ mt: 1, mb: 2 }} />
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        {[1,2,3,4,5].map(i => <Skeleton key={i} variant="rounded" height={72} />)}
      </Box>
    </Box>
  );
}


/**
 * EmbarcarBottomSheetContenido — Contenido del bottom sheet de embarque.
 *
 * Lee `reserva._salida.botes` (vienen del endpoint /muelle/dia) y muestra:
 *   - 0 botes  → Alert de error: hay que asignar embarcaciones a la salida.
 *   - 1 bote   → texto informativo, embarca directo (sin selector).
 *   - 2+ botes → chips selectores grandes (tap target ≥48px) con ocupación
 *                vigente; el menos lleno queda pre-seleccionado. El usuario
 *                debe tocar uno para confirmar.
 *
 * Si la reserva no tiene evidencia de check-in todavía, se muestra primero
 * `EvidenciaCaptura`. Tras capturar, se pasa al selector de bote.
 */
function EmbarcarBottomSheetContenido({ reserva, onCancel, onConfirmar, onError }) {
  const [evidenciaListo, setEvidenciaListo] = React.useState(!!reserva.tiene_evidencia_checkin);
  const [embarcacionId, setEmbarcacionId] = React.useState(null);

  const botes = React.useMemo(() => (reserva._salida?.botes || []), [reserva._salida]);
  const paxAEmbarcar = (reserva.adultos || 0) + (reserva.ninos || 0);

  // Pre-selección: el bote menos lleno con capacidad libre suficiente.
  React.useEffect(() => {
    if (embarcacionId != null || botes.length === 0) return;
    const candidatos = botes
      .map(b => ({ ...b, libres: Math.max((b.capacidad || 0) - (b.asignados || 0), 0) }))
      .filter(b => b.libres >= paxAEmbarcar);
    const ordenados = candidatos.length > 0
      ? candidatos.sort((a, b) => b.libres - a.libres)
      : botes;  // si ninguno tiene capacidad, mostramos todos igual con warning
    setEmbarcacionId(ordenados[0]?.embarcacion_id);
  }, [botes, embarcacionId, paxAEmbarcar]);

  // Captura de evidencia primero (sólo si falta)
  if (!evidenciaListo) {
    return (
      <Box sx={{ p: 2 }}>
        <EvidenciaCaptura
          reserva={reserva}
          onConfirm={() => setEvidenciaListo(true)}
          onCancel={onCancel}
          onError={onError}
        />
      </Box>
    );
  }

  if (botes.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          <strong>Esta salida no tiene embarcaciones asignadas.</strong><br />
          Ve a <em>Pasadías → Asignar Botes</em> y asigna al menos una embarcación
          antes de embarcar a los turistas.
        </Alert>
        <Button fullWidth onClick={onCancel} sx={{ textTransform: 'none' }}>Cerrar</Button>
      </Box>
    );
  }

  // Solo 1 bote — autoasignación + confirmar simple
  if (botes.length === 1) {
    const b = botes[0];
    const libres = Math.max((b.capacidad || 0) - (b.asignados || 0), 0);
    const cabe = libres >= paxAEmbarcar;
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info" sx={{ mb: 1.5, fontSize: 13 }}>
          Embarcará en <strong>{b.nombre}</strong>{' '}
          ({b.asignados || 0}/{b.capacidad} pax · {libres} libres)
        </Alert>
        <Typography sx={{ fontSize: 13, color: '#475569', mb: 2 }}>
          {formatNombre(reserva.nombre_cliente)} ({fmtPax(reserva)}) será marcado como embarcado.
        </Typography>
        {!cabe && (
          <Alert severity="warning" sx={{ mb: 1.5, fontSize: 12 }}>
            ⚠️ Capacidad excedida: {paxAEmbarcar} pax solicitados, {libres} libres.
            Considera revisar la asignación de capacidad.
          </Alert>
        )}
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button fullWidth onClick={onCancel} sx={{ textTransform: 'none', color: '#64748b' }}>
            Cancelar
          </Button>
          <Button fullWidth variant="contained" disableElevation
            onClick={() => onConfirmar(b.embarcacion_id)}
            sx={{ textTransform: 'none', fontWeight: 700,
                    background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
            Confirmar embarque
          </Button>
        </Box>
      </Box>
    );
  }

  // 2+ botes — selector con chips grandes
  return (
    <Box sx={{ p: 2 }}>
      <Typography sx={{ fontSize: 13, color: '#475569', mb: 1.5 }}>
        <strong>{formatNombre(reserva.nombre_cliente)}</strong> ({fmtPax(reserva)}) · selecciona
        en qué embarcación va a embarcar:
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
        {botes.map(b => {
          const libres = Math.max((b.capacidad || 0) - (b.asignados || 0), 0);
          const cabe = libres >= paxAEmbarcar;
          const seleccionado = b.embarcacion_id === embarcacionId;
          return (
            <Box key={b.embarcacion_id || b.id}
              onClick={() => setEmbarcacionId(b.embarcacion_id)}
              sx={{
                p: 1.5, borderRadius: 2,
                border: '2px solid',
                borderColor: seleccionado ? '#0d9488' : '#e2e8f0',
                bgcolor: seleccionado ? '#f0fdfa' : '#fff',
                cursor: 'pointer',
                minHeight: 56,
                display: 'flex', alignItems: 'center', gap: 1.5,
                opacity: cabe ? 1 : 0.6,
                transition: 'all 0.15s',
                '&:active': { transform: 'scale(0.98)' },
              }}>
              <DirectionsBoatIcon sx={{ fontSize: 22, color: seleccionado ? '#0d9488' : '#64748b' }} />
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#0f172a' }}>
                  {b.nombre}
                </Typography>
                <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                  {b.asignados || 0} de {b.capacidad} ocupados · <strong style={{ color: cabe ? '#16a34a' : '#dc2626' }}>{libres} libres</strong>
                </Typography>
              </Box>
              {!cabe && (
                <Chip size="small" label="No cabe"
                  sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#fee2e2', color: '#991b1b' }} />
              )}
              {seleccionado && cabe && (
                <Chip size="small" label="✓ Elegido"
                  sx={{ height: 18, fontSize: 9, fontWeight: 700, bgcolor: '#dcfce7', color: '#166534' }} />
              )}
            </Box>
          );
        })}
      </Box>
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button fullWidth onClick={onCancel} sx={{ textTransform: 'none', color: '#64748b' }}>
          Cancelar
        </Button>
        <Button fullWidth variant="contained" disableElevation
          disabled={!embarcacionId}
          onClick={() => onConfirmar(embarcacionId)}
          sx={{ textTransform: 'none', fontWeight: 700,
                  background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)' }}>
          Confirmar embarque
        </Button>
      </Box>
    </Box>
  );
}

/**
 * CharterDelDiaCard — tarjeta compacta de un charter (alquiler de embarcación)
 * mostrada arriba del listado de pasadías del muelle. Se distingue por chip
 * morado. Botones inline: Cobrar (deep-link a detalle con ?open=cobrar) y
 * Embarcar (PATCH /charter/{id}/estado con estado='embarcada'). El backend
 * bloquea Embarcar si saldo > 0.
 */
function CharterDelDiaCard({ charter }) {
  const queryClient = useQueryClient();
  const c = charter;
  const embarcado = c.estado_embarque === 'embarcado' || c.estado === 'embarcada';
  const saldo = c.saldo_a_cobrar_muelle ?? c.saldo_pendiente ?? 0;
  const [embarcando, setEmbarcando] = useState(false);
  const [snack, setSnack] = useState({ open: false, msg: '', severity: 'success' });

  const irACobrar = (e) => {
    e.stopPropagation();
    window.location.href = `/charter/${c.id}?open=cobrar`;
  };

  const embarcar = async (e) => {
    e.stopPropagation();
    if (embarcando) return;
    setEmbarcando(true);
    try {
      const r = await fetch(
        `${API_BASE}/turismo/charter/reservas/${c.id}/estado?empresa_id=${getEmpresaId()}`,
        {
          method: 'PATCH',
          headers: getAuthHeaders(),
          body: JSON.stringify({ estado: 'embarcada' }),
        }
      );
      const data = await r.json();
      if (!r.ok) {
        const msg = data?.detail?.message || data?.detail || 'Error al embarcar';
        setSnack({ open: true, msg: typeof msg === 'string' ? msg : JSON.stringify(msg), severity: 'error' });
        return;
      }
      setSnack({ open: true, msg: `Charter ${c.codigo_reserva} embarcado`, severity: 'success' });
      queryClient.invalidateQueries({ queryKey: ['muelle'] });
    } catch (err) {
      setSnack({ open: true, msg: 'Error de red', severity: 'error' });
    } finally { setEmbarcando(false); }
  };

  return (
    <>
    <Box
      onClick={() => { window.location.href = `/charter/${c.id}`; }}
      sx={{
        p: 1.5, borderRadius: 2, bgcolor: '#fff',
        border: '1px solid #e2e8f0', borderLeft: '4px solid #7c3aed',
        cursor: 'pointer', transition: 'all 0.15s',
        '&:hover': { boxShadow: '0 4px 12px rgba(124, 58, 237, 0.2)', transform: 'translateY(-1px)' },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <Chip label="CHARTER" size="small"
          sx={{ bgcolor: '#ede9fe', color: '#5b21b6', fontWeight: 800, fontSize: 9, height: 18 }}
        />
        <Typography sx={{ fontSize: 12, fontFamily: 'monospace', fontWeight: 700, color: '#7c3aed' }}>
          {c.codigo_reserva}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {embarcado ? (
          <Chip label="Embarcado" size="small" icon={<CheckCircleIcon sx={{ fontSize: 12 }} />}
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 10, height: 20 }}
          />
        ) : saldo > 0 ? (
          <Chip label={`Debe ${fmtMoney(saldo)}`} size="small"
            sx={{ bgcolor: '#fef3c7', color: '#92400e', fontWeight: 700, fontSize: 10, height: 20 }}
          />
        ) : (
          <Chip label="Pagado" size="small"
            sx={{ bgcolor: '#dcfce7', color: '#166534', fontWeight: 700, fontSize: 10, height: 20 }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#0f172a' }}>
          {c.nombre_cliente}
        </Typography>
        <Typography sx={{ fontSize: 11, color: '#64748b' }}>· {c.telefono || 's/tel'}</Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, fontSize: 12, color: '#475569', mb: embarcado ? 0 : 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <DirectionsBoatIcon sx={{ fontSize: 14, color: '#7c3aed' }} />
          {c.embarcacion_nombre || '—'}
        </Box>
        {(c.hora_zarpe || c.hora_regreso) && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <AccessTimeIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
            {c.hora_zarpe || '—'}{c.hora_regreso ? ` → ${c.hora_regreso}` : ''}
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <GroupsIcon sx={{ fontSize: 14, color: '#94a3b8' }} />
          {c.total_pax} pax
          {c.bebes > 0 ? ` · ${c.bebes} bebés` : ''}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontWeight: 700, color: '#0f172a' }}>
          {fmtMoney(c.precio_total)}
        </Box>
      </Box>

      {/* Botones inline de operación (ocultos si ya embarcó) */}
      {!embarcado && (
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          {saldo > 0 && (
            <Button size="small" variant="contained" startIcon={<PaymentIcon sx={{ fontSize: 16 }} />}
              onClick={irACobrar}
              sx={{
                textTransform: 'none', fontWeight: 700, fontSize: 12, flex: 1,
                background: 'linear-gradient(135deg, #16a34a 0%, #15803d 100%)',
                boxShadow: '0 2px 8px rgba(22, 163, 74, 0.35)',
                '&:hover': { background: 'linear-gradient(135deg, #15803d 0%, #14532d 100%)' },
              }}
            >
              Cobrar {fmtMoney(saldo)}
            </Button>
          )}
          <Button size="small" variant="contained" startIcon={<DirectionsBoatIcon sx={{ fontSize: 16 }} />}
            onClick={embarcar} disabled={saldo > 0 || embarcando}
            sx={{
              textTransform: 'none', fontWeight: 700, fontSize: 12, flex: 1,
              background: saldo > 0
                ? '#cbd5e1'
                : 'linear-gradient(135deg, #7c3aed 0%, #6d28d9 100%)',
              boxShadow: saldo > 0 ? 'none' : '0 2px 8px rgba(124, 58, 237, 0.35)',
              '&:hover': { background: saldo > 0
                ? '#cbd5e1'
                : 'linear-gradient(135deg, #6d28d9 0%, #5b21b6 100%)' },
            }}
          >
            {embarcando ? 'Embarcando…' : 'Embarcar'}
          </Button>
        </Box>
      )}
    </Box>
    <Snackbar open={snack.open} autoHideDuration={4000}
      onClose={() => setSnack(s => ({ ...s, open: false }))}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
    >
      <Alert severity={snack.severity} onClose={() => setSnack(s => ({ ...s, open: false }))}>
        {snack.msg}
      </Alert>
    </Snackbar>
    </>
  );
}
